import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ProductEnrichmentDashboard } from '@/components/product-enrichment/ProductEnrichmentDashboard';
import { ProductAEOViewer } from '@/components/aeo/ProductAEOViewer';
import { DemandWheel } from '@/components/sector/DemandWheel';
import { CategoryLeafDetail } from '@/components/sector/CategoryLeafDetail';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Package,
  ShoppingBag,
  Briefcase,
  FileText,
  Search,
  ExternalLink,
  Info,
  Tag,
  DollarSign,
  Star,
  Grid3x3,
  List,
  Image as ImageIcon,
  RefreshCw,
  Network,
  Sparkles,
  Loader2,
  ChevronRight,
  Target,
  Home,
  ChevronDown,
  ChevronUp,
  Users,
  HelpCircle,
  Eye,
  Globe,
  TrendingUp,
  CheckCircle2,
  FileText as FileTextIcon,
  Zap,
  Square,
  CheckSquare,
  Save,
  ThumbsUp,
  ThumbsDown,
  AlertTriangle,
  BarChart3,
  Shield,
  Trophy,
  ListChecks,
} from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { useSite } from '@/contexts/SiteContext';
import { useAuth } from '@/context/auth-context';
import { authFetch } from '@/utils/auth-fetch';
import { partitionByEnrichability, describeExcluded } from './enrichableSelection';
import { fingerprintSelection } from './selectionFingerprint';
import { cleanProductUrl } from '@/utils/url-cleaner';
import { AEOReadinessButton } from '@/components/context-readiness';
import { AEOFeedbackButton, AEOApprovalPanel, AEOQuestionsPanel } from '@/components/aeo';
import { VisibilityPanel } from '@/components/product/VisibilityPanel';
import { API_URL } from '@/config/api';
import { useToast } from '@/components/ui/use-toast';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useContentConfig } from '@/hooks/useContentConfig';
import type { ProductCategory, Product, Service, Article } from './TheOffering.shared';
import { formatComparisonValue } from './TheOffering.shared';
import { computePartialBatchOutcome, computeBlockedOutcome, computeSyncEnrichOutcome } from '@/utils/partialBatchOutcome';
import { useProductTarget } from '@/hooks/useProductTarget';
import { ProductTargetPicker } from '@/components/product-target/ProductTargetPicker';
import { applyPickerConfirm } from './pickerConfirm';
import { createRequestGate, createCommitGuard } from './listingRequestGate';
import { runListingRequest } from './listingRequest';

export function ProductOffering() {
  const { selectedSite } = useSite();
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get configured language from site content settings
  const { configurations } = useContentConfig({ siteId: selectedSite?.id });
  const configuredLanguage = configurations?.site?.language?.primaryLanguage || 'en';

  // Multi-sector support: sites can belong to multiple sectors
  // Users can switch between sectors to browse different category trees
  const [selectedSectorId, setSelectedSectorId] = useState<number | null>(null);
  const [availableSectors, setAvailableSectors] = useState<{id: number; name: string}[]>([]);

  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [articles, setArticles] = useState<Article[]>([]);
  const [pagination, setPagination] = useState<{ total: number; page: number; limit: number; pages: number } | null>(null);
  const [totalFeedProducts, setTotalFeedProducts] = useState<number>(0);  // Total products in product feed
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState<string | null>(null);
  const [enrichedFilter, setEnrichedFilter] = useState<'all' | 'enriched' | 'unenriched'>('all');
  const [aeoFilter, setAeoFilter] = useState<'all' | 'generated' | 'not_generated'>('all');
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('categories');

  // Product Enrichment state
  const [enrichingProduct, setEnrichingProduct] = useState<string | null>(null);
  const [showEnrichmentModal, setShowEnrichmentModal] = useState(false);
  const [enrichedProductData, setEnrichedProductData] = useState<any>(null);
  /**
   * ⛔⛔ THE MODAL'S PRODUCT ID, DERIVED FROM THE SHAPE THE ENDPOINT ACTUALLY RETURNS
   * (task-8ahq0c6onh, codex BLOCKING @e73d5a1ce).
   *
   * getEnrichedProduct returns `{ original: { id, … }, enriched: { id, … }, … }` — there is NO
   * top-level `product_id`. Both the new Re-run button and the PRE-EXISTING "Go to Enrichment
   * Review" link read `enrichedProductData.product_id`, so both were handling `undefined`: the
   * link has been navigating to `?product=undefined` all along, and my button would have posted
   * `productIds: [undefined]` and been rejected as an invalid UUID.
   *
   * 🔑 A REGEX TEST CANNOT SEE THIS. My single-path suite asserts the button EXISTS and is
   * invoked with force — both true of a button that passes undefined. The mismatch is between
   * two files' idea of a response shape, which only a test that carries a real response can
   * catch. That is the vacuous-assertion class I had flagged elsewhere in this same PR,
   * reproduced in my own test.
   */
  const enrichedProductModalId: string | undefined =
    enrichedProductData?.original?.id ?? enrichedProductData?.enriched?.id ?? undefined;

  // AEO Content Generation state (for products)
  const [generatingAEO, setGeneratingAEO] = useState<string | null>(null);
  const [showAEOModal, setShowAEOModal] = useState(false);
  const [aeoContent, setAeoContent] = useState<any>(null);
  const [aeoProgress, setAeoProgress] = useState<{ phase: string; percent: number } | null>(null);
  const [currentAEOProduct, setCurrentAEOProduct] = useState<any>(null);
  const [aeoConfirmProduct, setAeoConfirmProduct] = useState<any>(null);

  // SEO Visibility Panel state (Story 56)
  const [visibilityProduct, setVisibilityProduct] = useState<{ id: string; name: string; url: string } | null>(null);

  // Category AEO Content Generation state
  const [generatingCategoryAEO, setGeneratingCategoryAEO] = useState(false);
  const [showCategoryAEOModal, setShowCategoryAEOModal] = useState(false);
  const [categoryAEOContent, setCategoryAEOContent] = useState<any>(null);
  const [currentAEOCategory, setCurrentAEOCategory] = useState<string | null>(null);
  const [categoryAEOMetadata, setCategoryAEOMetadata] = useState<any>(null);
  const [savingCategoryAEO, setSavingCategoryAEO] = useState(false);
  const [categoryAEOFeedbackSummary, setCategoryAEOFeedbackSummary] = useState<any>(null);

  // Batch selection and enrichment state
  /**
   * ⛔ THE SHARED SELECTION STATE (F2 adoption). This surface used to own a private
   * `Set<string>` plus `allProductsSelected`, one of three vocabularies across three surfaces for
   * one idea. It now drives the shared hook, so toggle / select-visible / dedupe / clear have ONE
   * tested implementation instead of a copy per surface.
   *
   * ⚠️ idKind:'kbe' IS LOAD-BEARING, not decoration. These ids are knowledge_base_entries ids —
   * batch-enrich-async's parameter is named `productIds` and takes exactly those, and the route
   * records two agents losing work to the obvious misreading. The kind travels with the target so
   * a mismatch throws instead of returning success with zero work.
   *
   * ⚠️ THE HOOK'S COHORT MODE IS DELIBERATELY UNUSED HERE. batch-enrich-async accepts only
   * `categoryPath`, while this listing filters on search + category + subcategory + enriched + aeo
   * — so a cohort would drop four filters and SILENTLY WIDEN the operation. Until the backend
   * accepts the listing's filter (task-ny7t1n8v5c) select-all enumerates, and
   * `selectAllSnapshot` below records WHAT SELECT-ALL PRODUCED — a size and whether it was complete —
   * and whether the current selection still matches it is DERIVED, never stored. A dark-but-honest
   * affordance beats a live-but-lying one, and a derived claim beats a flag someone has to remember
   * to clear.
   */
  const target = useProductTarget({ idKind: 'kbe' });
  /** The canonical picker (search by name AND SKU, paste-a-list) — task-dx7eahgnvt. */
  const [pickerOpen, setPickerOpen] = useState(false);
  /** Non-null when the last listing request FAILED — so the empty state can say so (task-djasurv8m7). */
  const [listingError, setListingError] = useState<string | null>(null);
  const selectedIdSet = useMemo(() => new Set(target.selectedIds), [target.selectedIds]);
  const [batchEnriching, setBatchEnriching] = useState(false);
  /**
   * ⛔⛔ DERIVED, NOT MAINTAINED — and that change IS the fix.
   *
   * This was two booleans (`allProductsSelected`, `selectionIsComplete`) that every mutation site had
   * to remember to clear. Three sites remembered and the individual row toggle did not, so
   * select-all → untick one row left the badge saying "(all products)" over a selection that had
   * just changed size. I had fixed the page-level and outcome-narrowed paths ONE COMMIT EARLIER and
   * walked straight past the third.
   * 🔑 A flag that must be cleared by every future mutation WILL eventually meet a mutation that
   * forgets. So the claim is no longer stored: the snapshot records what select-all produced, and
   * whether the CURRENT selection still matches it is COMPUTED. Untick a row and the size stops
   * matching, so the badge disappears on its own — no site has to know it exists.
   */
  const [selectAllSnapshot, setSelectAllSnapshot] =
    useState<{ fingerprint: string; filterKey: string; complete: boolean } | null>(null);
  /**
   * ⛔⛔ WHETHER THAT CROSS-PAGE SELECTION IS ACTUALLY COMPLETE — and it is separate state because
   * "came from select-all" and "is everything that matches" ARE DIFFERENT FACTS, and conflating them
   * is what let a partial selection wear a complete label.
   * A MESSAGE IS READ ONCE; A STATUS PERSISTS. The toast said "6,143 excluded" or "the cap was
   * reached" — and then the operator dismissed it and the badge went on saying "(all products)"
   * for the rest of the session, contradicting it. The transient told the truth and the permanent
   * one lied, which is the same shape as this entire PR: excluding is not enough, the exclusion has
   * to be REPORTED — including by the thing still on screen ten minutes later.
   */

  const [loadingAllProductIds, setLoadingAllProductIds] = useState(false);
  const [forceReEnrich, setForceReEnrich] = useState(false); // Force re-enrichment of already enriched products

  // Category tree state (similar to TheSector.tsx)
  const [categoryTreeData, setCategoryTreeData] = useState<any[]>([]);
  const [categoryViewMode, setCategoryViewMode] = useState<'wheel' | 'tree'>('wheel');
  const [categoryBreadcrumb, setCategoryBreadcrumb] = useState<any[]>([]);
  const [currentCategoryView, setCurrentCategoryView] = useState<any>(null);
  const [loadingCategoryTree, setLoadingCategoryTree] = useState(false);
  const [categoryTreeStats, setCategoryTreeStats] = useState<any>(null);
  // Parent category info (for showing product count and question wheels at current level)
  const [currentLevelInfo, setCurrentLevelInfo] = useState<{
    product_count: number;
    enriched_count: number;
    category_path: string | null;
    sector_question_wheel: { id: string; name: string; question_count: number } | null;
    site_question_wheel: { id: string; name: string; question_count: number } | null;
  } | null>(null);

  // Category mapping state
  const [unmappedCategories, setUnmappedCategories] = useState<any[]>([]);
  const [orphanCategories, setOrphanCategories] = useState<any[]>([]);
  const [excludedCategories, setExcludedCategories] = useState<any[]>([]);
  const [activeMappings, setActiveMappings] = useState<any[]>([]);
  const [showActiveMappings, setShowActiveMappings] = useState(false);
  const [loadingMappings, setLoadingMappings] = useState(false);
  const [mappingInProgress, setMappingInProgress] = useState(false);
  const [mappingResults, setMappingResults] = useState<any>(null);
  const [mappingCategory, setMappingCategory] = useState<string | null>(null);
  const [excludingCategory, setExcludingCategory] = useState<string | null>(null);

  // Category detail panel state
  const [categoryDetailPanel, setCategoryDetailPanel] = useState<{
    open: boolean;
    category: any | null;
  }>({ open: false, category: null });

  // Personas state (from sector)
  const [personas, setPersonas] = useState<any[]>([]);
  const [loadingPersonas, setLoadingPersonas] = useState(false);

  // Multi-language support for personas
  const { language, translate } = useLanguage();
  const [translatedPersonas, setTranslatedPersonas] = useState<any[]>([]);
  const [isTranslatingPersonas, setIsTranslatingPersonas] = useState(false);

  // Read URL parameters on mount to set active tab and category filter
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const categoryParam = searchParams.get('category');

    // Set active tab from URL parameter
    if (tabParam && ['categories', 'products', 'services', 'articles', 'personas', 'quality', 'mapping'].includes(tabParam)) {
      setActiveTab(tabParam);
    }

    // Set category filter from URL parameter
    if (categoryParam) {
      setSelectedCategory(categoryParam);
      // Clear URL params after reading to avoid stale state
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('category');
      newParams.delete('tab');
      setSearchParams(newParams, { replace: true });
    }
  }, []); // Run only once on mount

  // Reset all site-specific state when site changes to prevent stale data from previous site
  const prevSiteIdRef = useRef<string | null>(null);
  /**
   * ⛔⛔ ONLY THE LATEST LISTING REQUEST MAY WRITE. prevSiteIdRef answers "same site?", not
   * "still the latest request?" — and same-site requests overlap on every keystroke, filter and
   * page. See listingRequestGate.ts for the out-of-order sequence this closes.
   */
  const listingGate = useRef(createRequestGate());
  useEffect(() => {
    if (!selectedSite?.id) return;
    // Skip the initial mount (no previous site yet)
    if (prevSiteIdRef.current && prevSiteIdRef.current !== selectedSite.id) {
      // Site actually changed — clear all stale state
      setCategoryTreeData([]);
      setSelectedCategory(null);
      setSelectedSubcategory(null);
      setCategoryBreadcrumb([]);
      setCurrentCategoryView(null);
      setCurrentLevelInfo(null);
      setCategoryDetailPanel({ open: false, category: null });
      setSelectedSectorId(null);
      setProducts([]);
      setCategories([]);
      setServices([]);
      setArticles([]);
      setPagination(null);
      // ⛔ THE BANNER IS SITE-SCOPED TOO (codex, leg 2, at 0718899f9). Without this, a failure on
      // site A survives the switch to site B and is read as B's: "The product list could not be
      // loaded" over B's empty listing, and if B's request is slow that misattribution is all the
      // operator sees. I ADDED listingError and did not add it here — which is the same shape as
      // the defect this PR fixes: a rule spread across a list of setters is one a new piece of
      // state can be added without.
      setListingError(null);
      setTotalFeedProducts(0);
      setCurrentPage(1);
      setSearchTerm('');
      setSearchQuery('');
      setUnmappedCategories([]);
      setOrphanCategories([]);
      setExcludedCategories([]);
      setActiveMappings([]);
      setPersonas([]);
      setInitialMarketSet(false);
    }
    prevSiteIdRef.current = selectedSite.id;
  }, [selectedSite?.id]);

  // Translate personas when language or data changes
  useEffect(() => {
    const translatePersonaData = async () => {
      if (language === 'en' || personas.length === 0) {
        setTranslatedPersonas(personas);
        return;
      }

      setIsTranslatingPersonas(true);
      try {
        const translated = await Promise.all(
          personas.map(async (persona, idx) => {
            const fieldsToTranslate = [
              { field: 'description', value: persona.description },
              { field: 'buying_behavior', value: persona.buying_behavior },
              { field: 'expectations', value: persona.expectations }
            ].filter(f => f.value);

            const translations = await Promise.all(
              fieldsToTranslate.map(async ({ field, value }) => ({
                field,
                translated: (await translate(value, 'buyer_persona', `persona_${idx}_${field}`)).translated
              }))
            );

            const translatedPersona = { ...persona };
            translations.forEach(({ field, translated }) => {
              translatedPersona[field] = translated;
            });
            return translatedPersona;
          })
        );
        setTranslatedPersonas(translated);
      } catch (error) {
        console.error('Error translating personas:', error);
        setTranslatedPersonas(personas);
      } finally {
        setIsTranslatingPersonas(false);
      }
    };

    translatePersonaData();
  }, [language, personas, translate]);

  // Data Quality stats (fetched from enrichment-analytics API)
  const [dataQualityStats, setDataQualityStats] = useState<{
    total_products: number;
    total_enriched: number;
    total_needs_review: number;
    overall_enrichment_pct: number;
    category_count: number;
  } | null>(null);
  const [loadingDataQuality, setLoadingDataQuality] = useState(false);

  // Market and Language switching state (mirroring TheSector.tsx)
  const [enrichedMarkets, setEnrichedMarkets] = useState<{market: string, market_code: string, categories_count: number, last_enriched: string}[]>([]);
  const [viewMarket, setViewMarket] = useState<string>('United States');
  const [displayLanguage, setDisplayLanguage] = useState<string>('original');
  const [categoryTranslations, setCategoryTranslations] = useState<Record<string, string>>({});
  const [isLoadingTranslations, setIsLoadingTranslations] = useState(false);
  const [initialMarketSet, setInitialMarketSet] = useState(false);

  // Load available sectors when site changes (from sector_ids array only, ignore deprecated sector_id)
  useEffect(() => {
    const loadAvailableSectors = async () => {
      if (!selectedSite?.sector_ids || selectedSite.sector_ids.length === 0) {
        setAvailableSectors([]);
        setSelectedSectorId(null);
        return;
      }

      try {
        // Fetch sector details for all assigned sectors
        const response = await authFetch(`/api/sectors`);
        if (response.ok) {
          const allSectors = await response.json();
          const siteSectors = allSectors.filter((s: any) =>
            selectedSite.sector_ids?.includes(s.id)
          );
          setAvailableSectors(siteSectors.map((s: any) => ({ id: s.id, name: s.name })));

          // Always select first sector for this site (reset on site change)
          if (siteSectors.length > 0) {
            setSelectedSectorId(siteSectors[0].id);
          }
        }
      } catch (error) {
        console.error('Error loading available sectors:', error);
      }
    };
    loadAvailableSectors();
  }, [selectedSite?.sector_ids]);

  useEffect(() => {
    if (selectedSite?.id) {
      loadOfferingData();
    }
  }, [selectedSite, currentPage, searchQuery, selectedCategory, selectedSubcategory, enrichedFilter, aeoFilter]);

  useEffect(() => {
    // Wait for selectedSectorId to be resolved before loading, otherwise this fires twice:
    // once with null sectorId, then again when sectors load and set it
    if (selectedSite?.id && activeTab === 'categories' && selectedSectorId) {
      loadCategoryTree();
    }
  }, [selectedSite, categoryViewMode, activeTab, selectedSectorId]);

  useEffect(() => {
    if (selectedSectorId && activeTab === 'personas') {
      loadPersonas();
    }
  }, [selectedSectorId, activeTab]);

  // Load data quality stats when quality tab is selected
  useEffect(() => {
    const loadDataQualityStats = async () => {
      if (!selectedSite?.id || activeTab !== 'quality') return;

      setLoadingDataQuality(true);
      try {
        const response = await authFetch(
          `${API_URL}/enrichment-analytics/site/${selectedSite.id}/category-readiness`
        );
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setDataQualityStats(data.data.summary);
          }
        }
      } catch (error) {
        console.error('Error loading data quality stats:', error);
      } finally {
        setLoadingDataQuality(false);
      }
    };
    loadDataQualityStats();
  }, [selectedSite?.id, activeTab]);

  // Load enriched markets when sector changes
  useEffect(() => {
    const loadEnrichedMarkets = async () => {
      if (!selectedSectorId) return;
      try {
        const response = await authFetch(`/api/sectors/${selectedSectorId}/enriched-markets`);
        if (response.ok) {
          const data = await response.json();
          setEnrichedMarkets(data.markets || []);
        }
      } catch (error) {
        console.error('Error loading enriched markets:', error);
      }
    };
    loadEnrichedMarkets();
  }, [selectedSectorId]);

  // Sync category display language with global language preference
  useEffect(() => {
    // When global language changes, update category display language
    // 'en' and 'en-US' map to 'original', other languages map directly
    const newDisplayLang = (language === 'en' || language === 'en-US') ? 'original' : language;
    if (displayLanguage !== newDisplayLang) {
      setDisplayLanguage(newDisplayLang);
    }
  }, [language]);

  // Load/create translations when display language changes
  useEffect(() => {
    const loadOrCreateTranslations = async () => {
      if (!selectedSectorId || displayLanguage === 'original') {
        setCategoryTranslations({});
        return;
      }

      setIsLoadingTranslations(true);
      console.log('[Offering Translations] Loading translations for language:', displayLanguage);
      try {
        const response = await authFetch(`/api/sectors/${selectedSectorId}/taxonomy/translations?language=${displayLanguage}`);
        if (response.ok) {
          const data = await response.json();
          const allRows = data.translations || [];
          const actualTranslations = allRows.filter((t: any) => t.translated_name != null);
          const missingTranslations = allRows.filter((t: any) => t.translated_name == null);

          // If there are missing translations OR no translations exist at all, create them on-demand
          const needsTranslation = missingTranslations.length > 0 || allRows.length === 0;
          if (needsTranslation) {
            const languageNames: Record<string, string> = {
              es: 'Spanish', de: 'German', fr: 'French', it: 'Italian'
            };
            const languageName = languageNames[displayLanguage] || displayLanguage;
            const translationCount = missingTranslations.length > 0 ? missingTranslations.length : 'all';
            const { dismiss } = toast({
              title: '��� Translating categories...',
              description: `Translating ${translationCount} category names to ${languageName}. This may take a moment...`,
              duration: 60000,
            });

            const createResponse = await authFetch(`/api/sectors/${selectedSectorId}/taxonomy/translations`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ language: displayLanguage })
            });

            dismiss();

            if (createResponse.ok) {
              const createData = await createResponse.json();
              const translationMap: Record<string, string> = {};
              (createData.translations || []).forEach((t: any) => {
                if (t.translated_name) {
                  translationMap[t.category_id] = t.translated_name;
                }
              });
              setCategoryTranslations(translationMap);
              toast({
                title: '✓ Translations ready',
                description: `${createData.translations_created} categories translated to ${languageName}`,
                duration: 5000,
              });
            }
          } else {
            const translationMap: Record<string, string> = {};
            actualTranslations.forEach((t: any) => {
              translationMap[t.category_id] = t.translated_name;
            });
            setCategoryTranslations(translationMap);
          }
        }
      } catch (error) {
        console.error('Error loading translations:', error);
      } finally {
        setIsLoadingTranslations(false);
      }
    };
    loadOrCreateTranslations();
  }, [selectedSectorId, displayLanguage]);

  // Reload category tree when view market changes
  useEffect(() => {
    if (!selectedSite?.id || !initialMarketSet) return;
    loadCategoryTree();
  }, [viewMarket]);

  // Mark initial market as set after enriched markets are loaded
  useEffect(() => {
    if (enrichedMarkets.length > 0 && !initialMarketSet) {
      setInitialMarketSet(true);
    }
  }, [enrichedMarkets]);

  const loadOfferingData = async () => {
    if (!selectedSite?.id) return;

    const requestSiteId = selectedSite.id;
    /**
     * ⛔⛔ THE ONE PLACE THAT DECIDES WHETHER THIS RESPONSE MAY TOUCH STATE.
     * The first version tested `isCurrent(seq)` at each of the three write points — the shape that
     * let the ORIGINAL defect exist, since a rule repeated per branch is a rule a fourth branch can
     * be added without. The second version had ONE check but ran it BEFORE `response.json()`, so a
     * superseded request could still wake from the parse and paint (codex, both legs, 13da40e9d).
     * ⇒ The guard now decides at the instant of the write, and the ordering it protects lives in
     *   `runListingRequest`, where a test can execute the interleaving rather than grep for it.
     * ⚠️ BOTH questions live here on purpose: same-site-but-superseded and different-site are
     *   different failures, and neither check implies the other. Both are re-read at write time —
     *   the operator can change site during the same parse.
     */
    const commitIfCurrent = createCommitGuard(
      listingGate.current,
      () => prevSiteIdRef.current === requestSiteId,
    );
    setLoading(true);
    const params = new URLSearchParams({
      page: currentPage.toString(),
      limit: '50',
    });
    if (searchQuery) {
      params.append('search', searchQuery);
    }
    if (selectedCategory) {
      params.append('category', selectedCategory);
    }
    if (selectedSubcategory) {
      params.append('subcategory', selectedSubcategory);
    }
    if (enrichedFilter !== 'all') {
      params.append('enriched', enrichedFilter === 'enriched' ? 'true' : 'false');
    }
    if (aeoFilter !== 'all') {
      params.append('aeo', aeoFilter === 'generated' ? 'true' : 'false');
    }

    await runListingRequest({
      fetchListing: () => authFetch(`/api/sites/${requestSiteId}/offering?${params}`),
      commit: commitIfCurrent,
      onSuccess: (data) => {
        setListingError(null);
        setCategories(data.categories || []);
        setProducts(data.products || []);
        setServices(data.services || []);
        setArticles(data.articles || []);
        setPagination(data.pagination || null);
        setTotalFeedProducts(data.totalFeedProducts || 0);
      },
      failureHandlers: { setProducts, setPagination, setListingError, toast },
      onSettled: () => setLoading(false),
    });
  };

  const refreshOffering = async () => {
    if (!selectedSite?.id) return;

    setRefreshing(true);
    try {
      const response = await authFetch(`/api/sites/${selectedSite.id}/offering/refresh`, {
        method: 'POST',
      });

      if (response.ok) {
        await loadOfferingData();
        toast({
          title: 'Success',
          description: 'Offering data has been refreshed',
        });
      }
    } catch (error) {
      console.error('Error refreshing offering:', error);
      toast({
        title: 'Error',
        description: 'Failed to refresh offering data',
        variant: 'destructive',
      });
    } finally {
      setRefreshing(false);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);

    // Clear previous timeout to properly debounce
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Debounce search - wait 800ms after user stops typing for comfortable typing
    searchTimeoutRef.current = setTimeout(() => {
      setSearchQuery(value);
      setCurrentPage(1); // Reset to first page on new search
    }, 800);
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleViewCategoryProducts = (categoryName: string) => {
    setSelectedCategory(categoryName);
    setCurrentPage(1); // Reset to first page
    setActiveTab('products'); // Switch to products tab
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const loadCategoryTree = async (parentPath?: string, parentId?: number) => {
    if (!selectedSite?.id) return;

    const requestSiteId = selectedSite.id;
    setLoadingCategoryTree(true);
    try {
      // HYBRID APPROACH: Load sector taxonomy for structure, enriched with site product data
      console.log('[LoadCategoryTree] Loading hybrid category tree, view mode:', categoryViewMode);

      // Step 1: Load sector taxonomy (for comprehensive category structure)
      let sectorCategories: any[] = [];
      if (selectedSectorId && !parentPath) {
        const taxonomyParams = new URLSearchParams();
        if (categoryViewMode === 'tree') taxonomyParams.append('tree', 'true');
        if (viewMarket && viewMarket !== 'United States') taxonomyParams.append('market', viewMarket);
        const queryStr = taxonomyParams.toString() ? `?${taxonomyParams.toString()}` : '';
        const sectorResponse = await authFetch(`${API_URL}/sectors/${selectedSectorId}/taxonomy${queryStr}`);
        if (sectorResponse.ok) {
          const sectorData = await sectorResponse.json();
          sectorCategories = sectorData.taxonomy || [];
          console.log('[LoadCategoryTree] Loaded sector taxonomy:', sectorCategories.length, 'categories for market:', viewMarket);
        }
      }

      // Step 2: Load site product data (for product counts and Spanish subcategories)
      const params = new URLSearchParams({
        view: categoryViewMode,
        ...(parentPath && { parentPath })
      });

      const siteResponse = await authFetch(`${API_URL}/sites/${selectedSite.id}/category-tree?${params}`);
      const siteData = await siteResponse.json();

      if (!siteData.success) {
        throw new Error('Failed to load site categories');
      }

      console.log('[LoadCategoryTree] Loaded site categories:', siteData.categories?.length || 0, 'categories');

      // Get the flat categoryLookup from API (contains ALL categories at any depth)
      const categoryLookup = siteData.categoryLookup || {};

      // Step 3: Merge sector structure with site product data
      const mergeCategories = (sectorCats: any[], siteCats: any[]): any[] => {
        // Build lookup map for site categories by name (case-insensitive)
        const siteMap = new Map<string, any>();
        const buildSiteMap = (cats: any[], parentPath: string = '') => {
          for (const cat of cats) {
            const key = cat.category_name.toLowerCase();
            siteMap.set(key, cat);
            // Also map by full path
            const fullKey = parentPath ? `${parentPath} > ${cat.category_name}`.toLowerCase() : key;
            siteMap.set(fullKey, cat);
            if (cat.children) {
              buildSiteMap(cat.children, cat.category_path || cat.category_name);
            }
          }
        };
        buildSiteMap(siteCats);

        // Recursively merge sector categories with site data
        const mergeCat = (sectorCat: any): any => {
          const key = sectorCat.category_name.toLowerCase();
          const siteCat = siteMap.get(key) || siteMap.get(sectorCat.category_path?.toLowerCase());

          // Fallback to categoryLookup when siteMap doesn't have the category
          // This handles cases where sector taxonomy root differs from site data root
          // (e.g., sector "Biometric Monitors" vs site root "Health & Beauty")
          let productCount = siteCat?.product_count || 0;
          let enrichedCount = siteCat?.enriched_count || 0;

          if (!siteCat && categoryLookup) {
            // Try lookup by category name first
            const lookupByName = categoryLookup[key];
            if (lookupByName) {
              productCount = lookupByName.product_count || 0;
              enrichedCount = lookupByName.enriched_count || 0;
              console.log(`[MergeCategories] Found "${sectorCat.category_name}" via categoryLookup: ${productCount} products`);
            } else if (sectorCat.category_path) {
              // Try lookup by full path (handles exact path matches)
              const lookupByPath = categoryLookup[sectorCat.category_path.toLowerCase()];
              if (lookupByPath) {
                productCount = lookupByPath.product_count || 0;
                enrichedCount = lookupByPath.enriched_count || 0;
                console.log(`[MergeCategories] Found "${sectorCat.category_name}" via path lookup: ${productCount} products`);
              }
            }
          }

          // Process children first so we can propagate their data up
          const mergedChildren = sectorCat.children ? sectorCat.children.map(mergeCat) : [];

          // Get search data from sector category, or sum from children if parent has none
          // Convert to number to avoid string concatenation issues
          let avgMonthlySearches = Number(sectorCat.avg_monthly_searches) || 0;
          let relativeDemandScore = Number(sectorCat.relative_demand_score) || 0;

          // If parent has no search data but children do, propagate children's data up
          if (avgMonthlySearches === 0 && mergedChildren.length > 0) {
            avgMonthlySearches = mergedChildren.reduce((sum: number, child: any) => sum + (child.avg_monthly_searches || 0), 0);
            relativeDemandScore = mergedChildren.reduce((sum: number, child: any) => sum + (child.relative_demand_score || 0), 0);
            if (avgMonthlySearches > 0) {
              console.log(`[MergeCategories] Propagated search data for "${sectorCat.category_name}": ${avgMonthlySearches} searches/mo from ${mergedChildren.length} children`);
            }
          }

          const merged = {
            id: `sector::${sectorCat.id}`,
            sector_category_id: sectorCat.id,
            category_name: sectorCat.category_name,
            category_path: sectorCat.category_path,
            // Sector research data (propagated from children if needed)
            relative_demand_score: relativeDemandScore,
            avg_monthly_searches: avgMonthlySearches,
            enriched: sectorCat.enriched || false,
            source_type: 'sector',
            // Site product data (if matched)
            product_count: productCount,
            enriched_count: enrichedCount,
            site_subcategories: [] as any[],
            // Pre-processed children
            children: mergedChildren,
            subcategory_count: 0,
            is_leaf: mergedChildren.length === 0
          };

          // Add site-specific subcategories (Spanish subcategories)
          if (siteCat?.children) {
            for (const siteChild of siteCat.children) {
              if (siteChild.source_type === 'site_specific') {
                merged.site_subcategories.push({
                  id: siteChild.id,
                  category_name: siteChild.category_name,
                  product_count: siteChild.product_count || 0,
                  source_type: 'site_specific',
                  mapped: siteChild.mapped
                });
              }
            }
          }

          // Calculate subcategory count (sector children + site subcategories)
          merged.subcategory_count = mergedChildren.length + merged.site_subcategories.length;

          return merged;
        };

        // If we have sector categories, use them as base
        if (sectorCats.length > 0) {
          return sectorCats.map(mergeCat);
        }

        // Fallback to site categories if no sector taxonomy
        return siteCats.map(cat => ({
          ...cat,
          site_subcategories: cat.children?.filter((c: any) => c.source_type === 'site_specific') || []
        }));
      };

      const mergedCategories = mergeCategories(sectorCategories, siteData.categories || []);
      console.log('[LoadCategoryTree] Merged categories:', mergedCategories.length);

      // Guard: discard response if site changed while request was in-flight
      if (prevSiteIdRef.current !== requestSiteId) return;

      setCategoryTreeData(mergedCategories);

      // Extract unmapped, orphan, and excluded categories from site data
      setUnmappedCategories(siteData.unmapped || []);
      setOrphanCategories(siteData.orphan || []);
      setExcludedCategories(siteData.excluded || []);
      setCategoryTreeStats(siteData.stats || null);

      // Update breadcrumb for wheel view
      if (categoryViewMode === 'wheel' && parentPath) {
        const category = siteData.parent;
        if (category && !categoryBreadcrumb.some(bc => bc.category_path === category.category_path)) {
          setCategoryBreadcrumb([...categoryBreadcrumb, category]);
        }
      }
    } catch (error) {
      console.error('Error loading category tree:', error);
      toast({
        title: 'Error',
        description: 'Failed to load category tree',
        variant: 'destructive'
      });
    } finally {
      setLoadingCategoryTree(false);
    }
  };

  const loadPersonas = async () => {
    if (!selectedSectorId) return;

    setLoadingPersonas(true);
    try {
      const response = await authFetch(`${API_URL}/sectors/${selectedSectorId}`);
      if (response.ok) {
        const data = await response.json();
        setPersonas(data.personas || []);
      }
    } catch (error) {
      console.error('Error loading personas:', error);
      toast({
        title: 'Error',
        description: 'Failed to load buyer personas',
        variant: 'destructive',
      });
    } finally {
      setLoadingPersonas(false);
    }
  };

  const handleCategoryClick = async (category: any) => {
    if (!selectedSite?.id) return;

    console.log('[CategoryClick] Category clicked:', category);

    // For wheel view, check if category has children by fetching them
    if (categoryViewMode === 'wheel') {
      try {
        // Check if this is a sector category (ID starts with 'sector::')
        if (category.id && category.id.startsWith('sector::')) {
          console.log('[CategoryClick] Sector category detected:', category.sector_category_id);

          // Load BOTH sector taxonomy children AND site product data
          const categoryPath = category.category_path || category.category_name;

          // Fetch sector children and site product data in parallel
          const [sectorResponse, siteResponse] = await Promise.all([
            authFetch(`${API_URL}/sectors/${selectedSectorId}/taxonomy?parentId=${category.sector_category_id}`),
            authFetch(`${API_URL}/sites/${selectedSite.id}/category-tree?view=wheel&parentPath=${encodeURIComponent(categoryPath)}`)
          ]);

          const sectorData = sectorResponse.ok ? await sectorResponse.json() : { taxonomy: [] };
          const siteData = siteResponse.ok ? await siteResponse.json() : { categories: [] };

          console.log('[CategoryClick] Sector children:', sectorData.taxonomy?.length || 0);
          console.log('[CategoryClick] Site children:', siteData.categories?.length || 0);

          // Build lookup map for site categories
          const siteMap = new Map<string, any>();
          for (const siteCat of (siteData.categories || [])) {
            siteMap.set(siteCat.category_name.toLowerCase(), siteCat);
            if (siteCat.category_path) {
              siteMap.set(siteCat.category_path.toLowerCase(), siteCat);
            }
          }

          // Merge sector children with site product data
          const mergedChildren = (sectorData.taxonomy || []).map((cat: any) => {
            const siteCat = siteMap.get(cat.category_name.toLowerCase()) ||
                           siteMap.get(cat.category_path?.toLowerCase());

            // Collect site-specific subcategories
            const siteSubcategories: any[] = [];
            if (siteCat?.children) {
              for (const child of siteCat.children) {
                if (child.source_type === 'site_specific') {
                  siteSubcategories.push({
                    id: child.id,
                    category_name: child.category_name,
                    product_count: child.product_count || 0,
                    source_type: 'site_specific',
                    mapped: child.mapped
                  });
                }
              }
            }

            return {
              id: `sector::${cat.id}`,
              sector_category_id: cat.id,
              category_name: cat.category_name,
              category_path: cat.category_path,
              // Site product data (merged)
              product_count: siteCat?.product_count || 0,
              enriched_count: siteCat?.enriched_count || 0,
              site_subcategories: siteSubcategories,
              // Sector research data
              relative_demand_score: cat.relative_demand_score || 0,
              avg_monthly_searches: cat.avg_monthly_searches || 0,
              subcategory_count: (cat.subcategory_count || 0) + siteSubcategories.length,
              source_type: 'sector',
              enriched: cat.enriched || false
            };
          });

          // If no sector children, load product feed categories instead
          if (mergedChildren.length === 0) {
            console.log('[CategoryClick] No sector children, using site categories for:', category.category_name);

            if (siteData.success && siteData.categories && siteData.categories.length > 0) {
              // Show product categories with site_subcategories
              const siteCategories = siteData.categories.map((cat: any) => ({
                ...cat,
                site_subcategories: cat.children?.filter((c: any) => c.source_type === 'site_specific') || []
              }));
              setCategoryTreeData(siteCategories);
              setCurrentCategoryView(category);
              setUnmappedCategories(siteData.unmapped || []);
              setOrphanCategories(siteData.orphan || []);

              // Store parent info for current level actions (product count, question wheels)
              if (siteData.parent) {
                setCurrentLevelInfo({
                  product_count: siteData.parent.product_count || 0,
                  enriched_count: siteData.parent.enriched_count || 0,
                  category_path: siteData.parent.category_path || null,
                  sector_question_wheel: siteData.parent.sector_question_wheel || null,
                  site_question_wheel: siteData.parent.site_question_wheel || null
                });
              }

              const categoryId = category.id;
              if (!categoryBreadcrumb.some(bc => bc.id === categoryId)) {
                setCategoryBreadcrumb([...categoryBreadcrumb, {
                  ...category,
                  // Preserve original category_path for breadcrumb navigation (not the sector ID)
                  category_path: category.category_path || categoryId,
                  id: category.id
                }]);
              }
              return;
            }

            // No product categories either - open research panel
            handleLeafCategoryClick(category);
            return;
          }

          // Has merged children - navigate into them
          setCategoryTreeData(mergedChildren);
          setCurrentCategoryView(category);
          setUnmappedCategories(siteData.unmapped || []);
          setOrphanCategories(siteData.orphan || []);

          // Store parent info for current level actions (product count, question wheels)
          if (siteData.parent) {
            setCurrentLevelInfo({
              product_count: siteData.parent.product_count || 0,
              enriched_count: siteData.parent.enriched_count || 0,
              category_path: siteData.parent.category_path || null,
              sector_question_wheel: siteData.parent.sector_question_wheel || null,
              site_question_wheel: siteData.parent.site_question_wheel || null
            });
          }

          const categoryId = category.id;
          if (!categoryBreadcrumb.some(bc => bc.id === categoryId)) {
            setCategoryBreadcrumb([...categoryBreadcrumb, {
              ...category,
              // Preserve original category_path for breadcrumb navigation (not the sector ID)
              category_path: category.category_path || categoryId,
              id: category.id
            }]);
          }
          return;
        }

        // Check if this is a site-specific subcategory (has '::' in ID but not 'sector::')
        // These are always leaf nodes and should open the detail panel
        if (category.id && category.id.includes('::') && !category.id.startsWith('sector::')) {
          console.log('[CategoryClick] Site-specific subcategory detected - opening detail panel');
          handleLeafCategoryClick(category);
          return;
        }

        // Make API call to fetch children from product feed
        // Use category.id as the path (wheel view uses id for the category path)
        const categoryPath = category.category_path || category.id;
        const params = new URLSearchParams({ view: 'wheel' });
        if (categoryPath) {
          params.append('parentPath', categoryPath);
        }

        console.log('[CategoryClick] Fetching children with params:', params.toString());
        const response = await authFetch(`${API_URL}/sites/${selectedSite.id}/category-tree?${params}`);
        const data = await response.json();
        console.log('[CategoryClick] Response:', data);

        if (data.success) {
          const children = (data.categories || []).map((cat: any) => ({
            ...cat,
            site_subcategories: cat.children?.filter((c: any) => c.source_type === 'site_specific') || []
          }));

          console.log('[CategoryClick] children:', children);
          console.log('[CategoryClick] children.length:', children.length);
          console.log('[CategoryClick] Is leaf check:', children.length === 0);

          // If no children, this is a leaf node - open detail panel
          if (children.length === 0) {
            console.log('[CategoryClick] Calling handleLeafCategoryClick...');
            handleLeafCategoryClick(category);
            return;
          }

          // Has children - navigate into category
          setCategoryTreeData(children);
          setCurrentCategoryView(category);
          setUnmappedCategories(data.unmapped || []);

          // Store parent info for current level actions (product count, question wheels)
          if (data.parent) {
            setCurrentLevelInfo({
              product_count: data.parent.product_count || 0,
              enriched_count: data.parent.enriched_count || 0,
              category_path: data.parent.category_path || null,
              sector_question_wheel: data.parent.sector_question_wheel || null,
              site_question_wheel: data.parent.site_question_wheel || null
            });
          }

          // Update breadcrumb - always add category since we're navigating into it
          // Use a unique identifier (id or category_path)
          const categoryId = category.category_path || category.id;
          if (!categoryBreadcrumb.some(bc => (bc.category_path || bc.id) === categoryId)) {
            setCategoryBreadcrumb([...categoryBreadcrumb, {
              ...category,
              category_path: categoryId,
              id: category.id
            }]);
          }
        }
      } catch (error) {
        console.error('Error checking category children:', error);
        toast({
          title: 'Error',
          description: 'Failed to load category children',
          variant: 'destructive'
        });
      }
    }
  };

  const handleLeafCategoryClick = async (category: any) => {
    if (!selectedSite?.id) return;

    console.log('[LeafClick] Opening detail for leaf category:', category);

    try {
      // If this is a sector category, use its sector_category_id directly
      if (category.id && category.id.startsWith('sector::') && category.sector_category_id) {
        console.log('[LeafClick] Sector category - using sector_category_id:', category.sector_category_id);

        // Open panel directly with sector category ID
        setCategoryDetailPanel({
          open: true,
          category: {
            id: category.sector_category_id, // Use sector category ID
            category_name: category.category_name,
            category_path: category.category_path || category.category_name,
            full_path: category.category_path || category.category_name,
            hasChildren: category.subcategory_count > 0,
          },
        });
        return;
      }

      // For product categories, look up matching sector template
      const categoryPath = encodeURIComponent(category.category_path || category.id || category.category_name);
      console.log('[LeafClick] Looking up template with path:', categoryPath);

      const response = await authFetch(`${API_URL}/sites/${selectedSite.id}/categories/${categoryPath}/template`);
      const data = await response.json();
      console.log('[LeafClick] Template lookup response:', data);

      // Always open the detail panel, regardless of whether template exists
      // If template exists, use its ID; otherwise use null and the panel will show "Generate Research"
      setCategoryDetailPanel({
        open: true,
        category: {
          id: data.template?.id || null, // null means no research data yet
          category_name: category.category_name,
          category_path: category.category_path || category.id || category.category_name,
          full_path: category.full_path || category.category_path || category.id,
          hasChildren: category.subcategory_count > 0,
        },
      });
    } catch (error) {
      console.error('Error looking up category template:', error);
      toast({
        title: 'Error',
        description: 'Failed to load category data',
        variant: 'destructive',
      });
    }
  };

  // Handle sector change - reset navigation to root
  const handleSectorChange = (newSectorId: number) => {
    setSelectedSectorId(newSectorId);
    // Reset category navigation when sector changes (different sector = different category tree)
    setCategoryBreadcrumb([]);
    setCurrentCategoryView(null);
    setCurrentLevelInfo(null);
  };

  const handleBreadcrumbClick = async (index: number) => {
    if (index === -1) {
      // Go back to root
      loadCategoryTree();
      setCategoryBreadcrumb([]);
      setCurrentCategoryView(null);
      setCurrentLevelInfo(null);
    } else {
      // Go back to specific breadcrumb level
      const category = categoryBreadcrumb[index];
      const newBreadcrumb = categoryBreadcrumb.slice(0, index);
      setCategoryBreadcrumb(newBreadcrumb);

      // Simulate clicking on that category again to rebuild the view
      await handleCategoryClick(category);
    }
  };

  const handleProductCountClick = (categoryPath: string, subcategory?: string) => {
    // Set the category/subcategory filter and switch to products tab
    if (subcategory) {
      setSelectedSubcategory(subcategory);
      setSelectedCategory(null);
    } else {
      setSelectedCategory(categoryPath);
      setSelectedSubcategory(null);
    }
    setCurrentPage(1);
    setActiveTab('products');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleMapSingleCategory = async (subcategoryName: string) => {
    if (!selectedSite?.id) return;

    setMappingCategory(subcategoryName);

    try {
      const response = await authFetch(`${API_URL}/sites/${selectedSite.id}/categories/map-subcategory`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subcategoryName,
          useAI: true
        })
      });

      const result = await response.json();

      if (result.success) {
        const mappedTo = result.mapping.google_parent_path.split(' > ').pop();
        toast({
          title: 'Mapping Complete!',
          description: `"${subcategoryName}" → "${mappedTo}". If incorrect, mark it as excluded.`,
        });

        // Reload category tree to show updated mapping
        await loadCategoryTree();
      } else {
        toast({
          title: 'Mapping Failed',
          description: result.error || 'Unknown error',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error mapping category:', error);
      toast({
        title: 'Error',
        description: 'Failed to map category',
        variant: 'destructive'
      });
    } finally {
      setMappingCategory(null);
    }
  };

  const handleMapAllCategories = async () => {
    if (!selectedSite?.id || unmappedCategories.length === 0) return;

    setMappingInProgress(true);
    setMappingResults({ mapped: 0, failed: 0, total: unmappedCategories.length });

    try {
      toast({
        title: 'AI Mapping Started',
        description: `Mapping ${unmappedCategories.length} categories using GPT-4...`
      });

      let mapped = 0;
      let failed = 0;

      for (const category of unmappedCategories) {
        try {
          const response = await authFetch(`${API_URL}/sites/${selectedSite.id}/categories/map-subcategory`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              subcategoryName: category.name,
              useAI: true
            })
          });

          const result = await response.json();
          if (result.success) {
            mapped++;
          } else {
            failed++;
          }

          // Update progress
          setMappingResults({ mapped, failed, total: unmappedCategories.length });
        } catch (error) {
          failed++;
          setMappingResults({ mapped, failed, total: unmappedCategories.length });
        }
      }

      toast({
        title: 'Batch Mapping Complete!',
        description: `Successfully mapped ${mapped} categories. ${failed} failed.`,
      });

      // Reload category tree to show all updated mappings
      await loadCategoryTree();
    } catch (error) {
      console.error('Error batch mapping categories:', error);
      toast({
        title: 'Error',
        description: 'Failed to map categories',
        variant: 'destructive'
      });
    } finally {
      setMappingInProgress(false);
    }
  };

  // Classify orphan categories (categories that don't follow Google taxonomy format)
  const handleClassifyAllOrphans = async () => {
    if (!selectedSite?.id || orphanCategories.length === 0) return;

    setMappingInProgress(true);
    setMappingResults({ mapped: 0, failed: 0, total: orphanCategories.length });

    try {
      toast({
        title: 'AI Classification Started',
        description: `Classifying ${orphanCategories.length} orphan categories using GPT-4...`
      });

      let classified = 0;
      let failed = 0;

      for (const category of orphanCategories) {
        try {
          const response = await authFetch(`${API_URL}/sites/${selectedSite.id}/categories/classify-orphan`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              categoryName: category.name,
              productCount: category.product_count
            })
          });

          const result = await response.json();
          if (result.success) {
            classified++;
          } else {
            failed++;
          }

          setMappingResults({ mapped: classified, failed, total: orphanCategories.length });
        } catch (error) {
          failed++;
          setMappingResults({ mapped: classified, failed, total: orphanCategories.length });
        }
      }

      toast({
        title: 'Classification Complete!',
        description: `Successfully classified ${classified} categories. ${failed} failed.`,
      });

      // Reload category tree to show updated classifications
      await loadCategoryTree();
    } catch (error) {
      console.error('Error classifying orphan categories:', error);
      toast({
        title: 'Error',
        description: 'Failed to classify categories',
        variant: 'destructive'
      });
    } finally {
      setMappingInProgress(false);
    }
  };

  const loadAllMappings = async () => {
    if (!selectedSite?.id) return;

    setLoadingMappings(true);
    try {
      const response = await authFetch(`${API_URL}/sites/${selectedSite.id}/categories/mappings`);
      const data = await response.json();

      if (data.success) {
        // Only show active (non-excluded) mappings
        setActiveMappings(data.mappings.filter((m: any) => !m.excluded));
      }
    } catch (error) {
      console.error('Error loading mappings:', error);
      toast({
        title: 'Error',
        description: 'Failed to load category mappings',
        variant: 'destructive'
      });
    } finally {
      setLoadingMappings(false);
    }
  };

  const handleToggleActiveMappings = async () => {
    if (!showActiveMappings) {
      await loadAllMappings();
    }
    setShowActiveMappings(!showActiveMappings);
  };

  const handleExcludeCategory = async (subcategoryName: string, exclude: boolean, reason?: string) => {
    if (!selectedSite?.id) return;

    setExcludingCategory(subcategoryName);

    try {
      const response = await authFetch(`${API_URL}/sites/${selectedSite.id}/categories/exclude-mapping`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subcategoryName,
          excluded: exclude,
          exclusionReason: reason || (exclude ? 'Incorrect AI mapping' : null)
        })
      });

      const result = await response.json();

      if (result.success) {
        toast({
          title: exclude ? 'Category Excluded' : 'Category Re-enabled',
          description: exclude
            ? `"${subcategoryName}" will not be used in question generation.`
            : `"${subcategoryName}" will be used in question generation again.`,
        });

        // Reload category tree to reflect changes
        await loadCategoryTree();

        // Reload active mappings if panel is open
        if (showActiveMappings) {
          await loadAllMappings();
        }
      } else {
        toast({
          title: 'Error',
          description: result.error || 'Failed to update category',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error excluding category:', error);
      toast({
        title: 'Error',
        description: 'Failed to update category',
        variant: 'destructive'
      });
    } finally {
      setExcludingCategory(null);
    }
  };

  /**
   * @param force skip the show-what-exists shortcut and RE-RUN enrichment.
   *
   * ⛔ WITHOUT THIS THE SINGLE-PRODUCT BUTTON COULD NEVER RE-ENRICH (S1,
   * task-8ahq0c6onh). The early return below shows the existing extraction whenever
   * ai_extracted_attributes > 0, and there was no other path — so an operator who had just
   * fixed a category's attributes, or resolved the semantic gaps that blocked a product,
   * had no way to make that one product run again. Their only recourse was to tick it in
   * the list and use the batch button, which is not where they are standing.
   */
  /**
   * Enrich ONE product.
   *
   * ⛔⛔ THE KIND IS A REQUIRED ARGUMENT, and that is the fix — the same one the row toggle needed.
   * This took `(productId: string)` and posted it straight to a knowledge_base_entries-only endpoint,
   * so the per-product "AI Enrich" button was a FIFTH way to send a feed id to an endpoint that
   * cannot take it: the checkboxes and both bulk paths were swept, and this one was not.
   * 🔑 A caller that does not know the row's provenance now cannot call this at all — omission is a
   * compile error rather than a silent wrong-namespace POST. That is the only version of the guard
   * that a future call site cannot forget.
   */
  const handleEnrichProduct = async (
    productId: string,
    idKind: string | null | undefined,
    force: boolean = false
  ) => {
    if (!selectedSite?.id) return;

    if (idKind !== 'kbe') {
      // ⇒ Names the state and the consequence. "Cannot enrich" alone reads as a broken button.
      toast({
        title: 'This product cannot be enriched yet',
        description: idKind
          ? 'It has no knowledge-base entry, so its feed has not been ingested. Enrichment takes '
            + 'knowledge-base ids and this request would be refused.'
          : 'Its source table is unknown, so there is no way to tell whether this action can target '
            + 'it. Refusing rather than guessing.',
        variant: 'destructive',
      });
      return;
    }

    setEnrichingProduct(productId);
    try {
      // First, try to get existing enriched data — unless we were asked to re-run.
      const enrichedResponse = force
        ? null
        : await authFetch(`${API_URL}/product-enrichment/${productId}`);

      if (enrichedResponse?.ok) {
        const enrichedData = await enrichedResponse.json();

        // Check if AI enrichment has been done (has ai_extracted_attributes > 0)
        // We check for AI-extracted specifically, not just any attribute_origins
        // because original feed attributes are now also included in attribute_origins
        if (enrichedData.data?.enrichment_stats?.ai_extracted_attributes > 0) {
          // AI enrichment already done, show it immediately
          setEnrichedProductData(enrichedData.data);
          setShowEnrichmentModal(true);
          setEnrichingProduct(null);
          return;
        }
      }

      // No existing data (or a forced re-run) — enrich through the SAME route the batch
      // button uses, so one product and twenty products cannot disagree about what
      // forceReEnrich means. A single id is below the route's internal threshold, so it
      // still answers synchronously with mode:'sync'.
      const response = await authFetch(`${API_URL}/product-enrichment/batch-enrich-async`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          siteId: selectedSite?.id,
          productIds: [productId],
          forceReEnrich: force,
        }),
      });

      if (response.ok) {
        const result = await response.json();
        // ⚠️ A 200 IS NOT A SUCCESS HERE. The route answers 200 with mode:'none' when the
        // product was already enriched and force was off, and mode:'sync' with blocked > 0
        // when a semantic gap stopped it. Announcing 'Product Enriched!' on any 2xx is how
        // a blocked product reported success — the same swallow the batch path had.
        const outcome = computeSyncEnrichOutcome(result);
        toast({
          title: outcome.title,
          description: outcome.description,
          ...(outcome.variant ? { variant: outcome.variant } : {}),
        });
        if (!outcome.refresh) {
          setEnrichingProduct(null);
          return;
        }

        // Get the newly enriched data
        const newEnrichedResponse = await authFetch(`${API_URL}/product-enrichment/${productId}`);
        if (newEnrichedResponse.ok) {
          const newEnrichedData = await newEnrichedResponse.json();
          setEnrichedProductData(newEnrichedData.data);
          setShowEnrichmentModal(true);
        }

        // Refresh product list to update enrichment status and enable AEO button
        await loadOfferingData();
      } else {
        // The route returns 409 with the per-product reason (semantic-gap block, gate
        // error). Throwing a fixed string discarded it and left the operator with
        // 'Failed to enrich product' for a condition they could have acted on.
        const error = await response.json().catch(() => ({}));
        const outcome = error?.mode === 'sync'
          ? computeSyncEnrichOutcome(error)
          : computeBlockedOutcome(error);
        toast({
          title: outcome.title,
          description: outcome.description,
          variant: 'destructive',
        });
        setEnrichingProduct(null);
        return;
      }
    } catch (error) {
      console.error('Error enriching product:', error);
      toast({
        title: 'Error',
        description: 'Failed to enrich product. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setEnrichingProduct(null);
    }
  };

  /**
   * Toggle one row's selection.
   *
   * ⛔⛔ TAKES THE ROW, NOT THE ID — AND THAT SIGNATURE CHANGE *IS* THE FIX. It used to be
   * `(productId: string)`, which DISCARDS THE ONE FIELD NEEDED TO BE SAFE: `id` alone cannot say
   * which table it came from, so no amount of care inside this function could have checked. The
   * bulk paths were filtered by id_kind and this one could not be, which is exactly how an operator
   * could still hand-pick a feed-only row and refuse the whole batch — one row at a time instead of
   * six thousand at once.
   * 🔑 Make the witness a required argument. A caller that has only an id now fails to compile
   * rather than silently skipping the check.
   */
  const toggleProductSelection = (product: Product) => {
    if (product.id_kind !== 'kbe') {
      // ⇒ The checkbox is also disabled for these rows; this is the mechanism behind that
      // affordance, not a duplicate of it. A disabled control can be defeated (keyboard, a future
      // refactor, a caller that forgets); the guard cannot.
      toast({
        title: 'This product cannot be enriched yet',
        description: `"${product.name}" has no knowledge-base entry, so its feed has not been `
          + 'ingested. Enrichment takes knowledge-base ids, and including this row would cause the '
          + 'ENTIRE batch to be refused.',
        variant: 'destructive',
      });
      return;
    }
    target.toggle(product.id);
  };

  /**
   * Apply a selection decision returned by an outcome helper.
   *
   * ⚠️ THIS EXISTS BECAUSE THE SAME TWO LINES APPEARED FOUR TIMES VERBATIM — the narrow/clear pair
   * after a partial, a blocked, a sync and an error outcome. Four copies of a two-line rule is the
   * same per-branch duplication that took four review rounds to stamp out of the response shapes
   * one layer down; collapsing it here means a fifth outcome branch inherits the behaviour rather
   * than having to remember it.
   */
  /**
   * ⚠️ NO id_kind CHECK HERE, AND THAT IS DELIBERATE — recorded because I had to derive it, and the
   * next reader should not have to. These ids come back from an enrichment RESPONSE: they were in a
   * request that the kbe-only endpoint accepted, so they are knowledge_base_entries ids by
   * construction. Re-filtering them would be theatre, and filtering on a field the response does not
   * carry would silently empty the selection.
   * ⇒ THE INVARIANT THIS RESTS ON: every path that BUILDS a selection filters by id_kind first
   * (row toggle, both bulk selects, the picker). If a future outcome helper ever returns ids that did
   * not round-trip through the endpoint, this becomes a hole — so that helper must filter, not this.
   */
  const applySelection = (ids: string[]) => {
    if (ids.length === 0) target.clear();
    else target.setExplicitIds(ids);
    setSelectAllSnapshot(null);   // ⇐ an outcome-narrowed selection is not what select-all produced
  };

  // Handle AEO content generation
  const handleGenerateAEOContent = async (product: any, forceRegenerate: boolean = false) => {
    const productId = product.product_feed_id || product.id;
    setGeneratingAEO(productId);
    setCurrentAEOProduct(product);
    setAeoProgress({ phase: 'Starting', percent: 0 });
    setAeoContent(null);

    // Keep the requested member identity for regeneration, and remember separately
    // which product owns the content returned by the backend.
    const rememberSavedContent = (content: any, contentProductId: string) => {
      setAeoContent(content);
      setCurrentAEOProduct({ ...product, has_aeo_content: true, aeo_content_product_id: contentProductId });
      setProducts(prev => prev.map(p =>
        (p.product_feed_id || p.id) === productId
          ? { ...p, has_aeo_content: true, aeo_content_product_id: contentProductId }
          : p
      ));
    };

    try {
      if (product.aeo_content_error) throw new Error(product.aeo_content_error);
      // Show progress modal
      setShowAEOModal(true);

      // Existing content is a read, even when this product can no longer generate.
      if (product.has_aeo_content && !forceRegenerate) {
        setAeoProgress({ phase: 'Loading saved content...', percent: 5 });
        const contentProductId = product.aeo_content_product_id || productId;
        const response = await authFetch(`${API_URL}/product-enrichment/${contentProductId}/aeo-page`);
        const saved = await response.json();
        if (!response.ok || !saved.success || !saved.data) {
          throw new Error(saved.error || 'Saved AEO content is unavailable');
        }
        rememberSavedContent(saved.data, saved.contentProductId || contentProductId);
        setGeneratingAEO(null);
        setAeoProgress(null);
        return;
      }

      // Explicit generation uses the durable async pipeline.
      setAeoProgress({ phase: 'Checking cache...', percent: 5 });

      const response = await authFetch(`${API_URL}/product-enrichment/${productId}/aeo-page`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          language: configuredLanguage,
          includeComparisons: true,
          maxComparisons: 3,
          maxQAs: 10,
          format: 'json',
          forceRegenerate,
          async: true  // Always async - avoids Cloudflare 504 timeout
        })
      });

      const jobResult = await response.json();
      if (!response.ok || jobResult.success === false) {
        throw new Error(jobResult.error || 'Failed to create AEO generation job');
      }

      if (jobResult.cached) {
        // Cache hit - backend returned cached content directly
        setAeoProgress({ phase: 'Complete', percent: 100 });
        rememberSavedContent(jobResult.data, jobResult.canonicalId || jobResult.productId || productId);
        toast({
          title: 'AEO Content Loaded!',
          description: 'Loaded cached content. Click Regenerate for fresh content.',
        });
        setGeneratingAEO(null);
        setAeoProgress(null);
        return;
      }

      if (!jobResult.async || !jobResult.jobId) {
        // Synchronous response (shouldn't happen with async: true, but handle it)
        setAeoProgress({ phase: 'Complete', percent: 100 });
        rememberSavedContent(jobResult.data, jobResult.canonicalId || jobResult.productId || productId);
        toast({
          title: 'AEO Content Generated!',
          description: 'Product page content has been optimized for AI search engines.',
        });
        setGeneratingAEO(null);
        setAeoProgress(null);
        return;
      }

      // Poll for job completion
      const jobId = jobResult.jobId;
      let pollInFlight = false;
      let pollingStopped = false;
      setAeoProgress({ phase: 'Processing...', percent: 10 });

      const pollInterval = setInterval(async () => {
        if (pollInFlight || pollingStopped) return;
        pollInFlight = true;
        try {
          const jobStatusResponse = await authFetch(`${API_URL}/jobs/${jobId}`);
          if (!jobStatusResponse.ok) {
            throw new Error('Failed to check job status');
          }

          const jobStatus = await jobStatusResponse.json();

          // Update progress
          setAeoProgress({
            phase: jobStatus.progress?.message || 'Processing...',
            percent: jobStatus.progress?.percent || 50
          });

          if (jobStatus.status === 'completed') {
            // A completed worker job can carry a terminal generation/save refusal.
            // Fetching with async:true here would create another job and retry it.
            if (jobStatus.result?.success === false) {
              throw new Error(jobStatus.result.error || jobStatus.result.reason || 'AEO generation failed');
            }
            setAeoProgress({ phase: 'Loading content...', percent: 95 });

            // Read the completed job's owner, without creating a replacement job.
            const contentProductId = jobStatus.result?.canonicalId || jobStatus.result?.productId || productId;
            const contentResponse = await authFetch(`${API_URL}/product-enrichment/${contentProductId}/aeo-page`);
            const contentResult = await contentResponse.json();
            if (!contentResponse.ok || contentResult.success === false || !contentResult.data) {
              throw new Error(contentResult.error || 'Generated content is unavailable');
            }
            pollingStopped = true;
            clearInterval(pollInterval);
            setAeoProgress({ phase: 'Complete', percent: 100 });
            rememberSavedContent(contentResult.data, contentResult.contentProductId || contentProductId);

            toast({
              title: 'AEO Content Generated!',
              description: 'Product page content has been optimized for AI search engines.',
            });
            setGeneratingAEO(null);
            setAeoProgress(null);
          } else if (['failed', 'cancelled', 'expired'].includes(jobStatus.status)) {
            throw new Error(jobStatus.error || 'AEO generation failed');
          }
        } catch (pollError) {
          pollingStopped = true;
          clearInterval(pollInterval);
          console.error('Error polling job status:', pollError);
          toast({
            title: 'Error',
            description: pollError instanceof Error ? pollError.message : 'Failed to check generation progress. Please try again.',
            variant: 'destructive',
          });
          setShowAEOModal(false);
          setGeneratingAEO(null);
          setAeoProgress(null);
        } finally {
          pollInFlight = false;
        }
      }, 3000); // Poll every 3 seconds

    } catch (error) {
      console.error('Error generating AEO content:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to load AEO content. Please try again.',
        variant: 'destructive',
      });
      setShowAEOModal(false);
      setGeneratingAEO(null);
      setAeoProgress(null);
    }
  };

  // Generate AEO content for a category
  const handleGenerateCategoryAEO = async (categoryPath: string, force: boolean = false) => {
    if (!selectedSite?.id || !categoryPath) return;

    setGeneratingCategoryAEO(true);
    setCurrentAEOCategory(categoryPath);
    setCategoryAEOContent(null);
    setCategoryAEOMetadata(null);
    setCategoryAEOFeedbackSummary(null);
    setShowCategoryAEOModal(true);

    try {
      const response = await authFetch(`${API_URL}/category/aeo-page`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          siteId: selectedSite.id,
          categoryPath,
          language: configuredLanguage,
          force
        })
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setCategoryAEOContent(result.data);

          // If content has persistence_id, load metadata and feedback
          if (result.data.persistence_id) {
            setCategoryAEOMetadata({
              id: result.data.persistence_id,
              status: result.data.status || 'draft',
              version: result.data.version || 1,
              createdAt: result.data.generated_at,
              updatedAt: result.data.generated_at
            });

            // Fetch feedback summary
            try {
              const feedbackRes = await authFetch(`${API_URL}/aeo-feedback/category/${result.data.persistence_id}/summary`);
              if (feedbackRes.ok) {
                const feedbackData = await feedbackRes.json();
                setCategoryAEOFeedbackSummary(feedbackData.data?.totals);
              }
            } catch (e) {
              console.error('Error fetching feedback summary:', e);
            }
          }

          toast({
            title: result.data.from_cache ? 'AEO Content Loaded!' : 'AEO Content Generated!',
            description: result.data.from_cache
              ? 'Loaded cached content. Click Regenerate for fresh content.'
              : 'Category page content has been optimized for AI search engines.',
          });
        } else {
          throw new Error(result.error || 'Failed to generate');
        }
      } else {
        throw new Error('Failed to generate AEO content');
      }
    } catch (error) {
      console.error('Error generating category AEO content:', error);
      toast({
        title: 'Error',
        description: 'Failed to generate category AEO content. Please try again.',
        variant: 'destructive',
      });
      setShowCategoryAEOModal(false);
    } finally {
      setGeneratingCategoryAEO(false);
    }
  };

  // Save category AEO content to persistence
  const handleSaveCategoryAEO = async () => {
    if (!selectedSite?.id || !currentAEOCategory || !categoryAEOContent) return;

    setSavingCategoryAEO(true);
    try {
      const response = await authFetch(`${API_URL}/category/aeo-content`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          siteId: selectedSite.id,
          categoryPath: currentAEOCategory,
          language: configuredLanguage,
          content: categoryAEOContent.sections || categoryAEOContent
        })
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setCategoryAEOMetadata(result.data);
          toast({
            title: 'Content Saved!',
            description: 'AEO content has been saved and is ready for review.',
          });
        }
      }
    } catch (error) {
      console.error('Error saving category AEO content:', error);
      toast({
        title: 'Error',
        description: 'Failed to save content. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSavingCategoryAEO(false);
    }
  };

  // Handle category AEO status change
  const handleCategoryAEOStatusChange = (newStatus: string) => {
    if (categoryAEOMetadata) {
      setCategoryAEOMetadata({ ...categoryAEOMetadata, status: newStatus });
    }
  };

  // Products are now filtered server-side, no need for client-side filtering
  const filteredProducts = products;

  // Check if all visible products are selected
// ⇒ Only rows the enrichment endpoint can consume. A row whose id came from product_feed_items
  // cannot be enriched until its feed is ingested, and including it refuses the WHOLE batch.
  /**
   * ⇒ THE BADGE'S ENTIRE CONDITION, computed from the selection itself. If the operator unticks one
   * row the size stops matching and this goes false — no mutation site has to remember anything.
   */
  const currentFilterKey = JSON.stringify([
    selectedSite?.id, searchQuery, selectedCategory, selectedSubcategory, enrichedFilter, aeoFilter,
  ]);
  const filterKeyRef = useRef(currentFilterKey);
  useEffect(() => { filterKeyRef.current = currentFilterKey; });

  /**
   * ⛔⛔ IDENTITY, NOT LENGTH — and the previous version compared only the count. Replace one selected
   * product with another and the count is restored, so the badge reappeared over a set that was no
   * longer what select-all returned. Change the filter and the old snapshot went on claiming "all
   * products" for a view it had nothing to do with.
   * ⇒ So the snapshot carries WHICH products (an order-independent fingerprint) and WHICH VIEW (the
   *   filter key). Either drifting makes the badge disappear on its own, with no mutation site
   *   needing to know it exists.
   * ⚠️ Memoised on the array reference: the hook rebuilds its array on every change, so this
   *   recomputes exactly when the selection actually changes and not once per render.
   */
  const selectionFingerprint = useMemo(
    () => fingerprintSelection(target.selectedIds),
    [target.selectedIds]
  );
  const selectionMatchesSelectAll =
    selectAllSnapshot !== null
    && selectionFingerprint === selectAllSnapshot.fingerprint
    && currentFilterKey === selectAllSnapshot.filterKey;
  const selectionIsComplete = selectionMatchesSelectAll && selectAllSnapshot!.complete;

  const enrichableVisible = filteredProducts.filter((p) => p.id_kind === 'kbe');
  // ⚠️ AN UNKNOWN KIND IS NOT A KNOWN FEED ROW. Both are excluded, but telling the operator "no
  // knowledge-base entry" about a row whose provenance is MISSING is a confident wrong diagnosis —
  // the same "do not silently diagnose an ambiguous value" the partition helper throws over.
  const feedOnlyVisible = filteredProducts.filter((p) => p.id_kind === 'feed').length;
  const unknownKindVisible = filteredProducts.filter((p) => !p.id_kind).length;
  const notEnrichableCount = filteredProducts.length - enrichableVisible.length;
  // ⚠️ Measured against the ENRICHABLE rows, not all rows — otherwise the header checkbox can never
  // read as "all selected" on a site like Motocard and the operator keeps clicking it.
  const allVisibleSelected = enrichableVisible.length > 0 &&
    enrichableVisible.every((p) => selectedIdSet.has(p.id))

  // Total products count from pagination
  const totalProductsCount = pagination?.total || filteredProducts.length;

  // Select all visible products (current page)
  const selectAllProducts = () => {
    target.selectVisible(enrichableVisible.map((p) => p.id));
    setSelectAllSnapshot(null);   // ⇐ a page is never "all products"
    if (notEnrichableCount > 0) {
      // ⇒ Said out loud on the page-level select too. A silently shorter selection is the failure
      // mode being removed, not a tidier version of it.
      const reasons: string[] = [];
      if (feedOnlyVisible > 0) {
        reasons.push(`${feedOnlyVisible} have no knowledge-base entry, so their feed has not been ingested`);
      }
      if (unknownKindVisible > 0) {
        reasons.push(`${unknownKindVisible} arrived without a source table, so it cannot be determined whether this action can target them`);
      }
      toast({
        title: `${notEnrichableCount} product(s) on this page cannot be enriched yet`,
        description: `${reasons.join('; ')}. They were not selected.`,
      });
    }
  };

  /**
   * ⛔⛔ SELECT-ALL IS A REQUEST WHOSE ANSWER CAN OUTLIVE THE QUESTION.
   * /offering/ids is slow on a large site. Change the search or a filter while it is in flight and
   * the OLD result installs itself beneath the NEW visible listing — "Enrich Selected" then runs
   * against products the operator is no longer looking at, with the screen showing something else
   * entirely. Nothing on screen says the selection belongs to a filter that is gone.
   * 🔑 SAME CLASS AS THE PICKER'S requestSeq/appliedSearch GUARD, ONE FILE OVER. I fixed it there,
   * in this same PR, and did not carry it here — the recurring shape of this whole review has been
   * fixing one instance of a class and leaving its twin.
   * ⇒ The key is the SITE PLUS EVERY FILTER, because any of them changes which products match.
   */
  const selectAllSeq = useRef(0);
  /** Synchronous twin of `loadingAllProductIds` — see the guard in selectAllProductsAcrossPages. */
  const selectAllInFlightRef = useRef(false);

  /**
   * ⛔⛔ THE LATEST FILTER KEY LIVES IN A REF, AND THE FIRST VERSION OF THIS GUARD WAS INERT WITHOUT
   * IT. I rebuilt `currentKey` after the await from the SAME RENDER CLOSURE that built `requestKey` —
   * so both read identical captured values, the comparison was always equal, and THE GUARD COULD
   * NEVER FIRE. It read like a race fix and prevented nothing.
   * 🔑 Third time in this PR that a guard was written against a closure instead of the current value
   * (the submit re-entrancy flag, the cohort check in the picker's catch, and now this). The shape
   * to grep for is: a value READ AFTER AN AWAIT that was CAPTURED BEFORE IT.
   * ⚠️ And a guard that cannot fire is worse than no guard, because it stops anyone looking.
   */

  // Select ALL products across all pages
  const selectAllProductsAcrossPages = async () => {
    if (!selectedSite?.id) return;

    /**
     * ⛔⛔ SYNCHRONOUS RE-ENTRY GUARD, for the same reason the picker's confirm needed one: two clicks
     * dispatched before React commits the disabled state both see the old render's values. `disabled`
     * is the affordance; this is the mechanism. Select-all can fetch 500,000 ids, so a duplicate is
     * not merely untidy.
     */
    if (selectAllInFlightRef.current) return;
    selectAllInFlightRef.current = true;

    const requestKey = currentFilterKey;
    const seq = ++selectAllSeq.current;

    setLoadingAllProductIds(true);
    try {
      // Use lightweight IDs-only endpoint for better performance
      const params = new URLSearchParams();
      // ⚠️ THE CAP IS NAMED, AND ITS EFFECT IS REPORTED BELOW (F2 adoption). A bare 500000 here is a
      // SILENT TRUNCATION: a site with more matching products returns exactly the cap, the operator
      // is told "Selected 500,000 products across all pages", and the remainder never runs with
      // nothing anywhere saying so.
      const SELECT_ALL_CAP = 500000;
      params.append('limit', String(SELECT_ALL_CAP));
      if (searchQuery) params.append('search', searchQuery);
      if (selectedCategory) params.append('category', selectedCategory);
      if (selectedSubcategory) params.append('subcategory', selectedSubcategory);
      if (enrichedFilter !== 'all') {
        params.append('enriched', enrichedFilter === 'enriched' ? 'true' : 'false');
      }
      if (aeoFilter !== 'all') {
        params.append('aeo', aeoFilter === 'generated' ? 'true' : 'false');
      }

      const response = await authFetch(`/api/sites/${selectedSite.id}/offering/ids?${params}`);
      // ⛔ A NON-2xx USED TO FALL OUT OF THIS `if` WITH NO `else` — the classic silent fallback. The
      // old selection stayed on screen, no toast fired, and the operator concluded the click had not
      // registered rather than that select-all had FAILED. Then they press Enrich on whatever was
      // selected before, believing it is the set they just asked for.
      // ⇒ Thrown, not toasted here, because the catch below already reports failures for this action;
      // a second, parallel error path is how the two drift apart.
      if (!response.ok) {
        throw new Error(
          `Could not select all matching products (HTTP ${response.status}). Nothing was selected — `
          + 'the previous selection is unchanged.'
        );
      }
      {
        const data = await response.json();
        /**
         * ⇒ DISCARD A RESULT WHOSE QUESTION HAS CHANGED. Compared on BOTH the sequence and the key:
         * the sequence catches a second select-all, and the key catches a filter change that did not
         * trigger one. Either alone leaves a hole.
         */
        // ⇒ THE REF, not a rebuild from this closure. filterKeyRef is updated by an effect on every
        // render, so it reports what the filters are NOW — which is the entire question being asked.
        if (seq !== selectAllSeq.current || filterKeyRef.current !== requestKey) {
          toast({
            title: 'Selection discarded — the filters changed while it was loading',
            description: 'Nothing was selected. Press "Select all" again now that the list has '
              + 'settled, so the selection matches what you can see.',
            variant: 'destructive',
          });
          return;
        }
        /**
         * ⛔⛔ BOTH PROPERTIES SURVIVE THIS MERGE, AND THEY ARE INDEPENDENT.
         *   (1) VALIDATE THE SHAPE — a missing field must not become a valid EMPTY selection. That
         *       reported "Selected 0 products" as a success and erased the previous selection.
         *   (2) CONSUME THE PROVENANCE — batch-enrich-async takes knowledge_base_entries ids and is
         *       ALL-OR-NOTHING, so feed-only ids refuse the WHOLE batch (Motocard: 6,143 of 8,201).
         * ⇒ The validation now targets `items` rather than `ids`, because `items` is the field that
         *   carries each id's source table; `ids` alone cannot answer the question that matters.
         * ⚠️ THROWS rather than falling back to `ids`. A fallback would silently restore the exact
         *   defect the moment the two sides drift, and it would look like it was working.
         */
        if (!Array.isArray(data?.items)) {
          throw new Error(
            'The select-all response did not include `items` with each id\'s source table, so there '
            + 'is no way to tell which products can actually be enriched. Nothing was selected — the '
            + 'previous selection is unchanged.'
          );
        }
        const partition = partitionByEnrichability(data.items, 'kbe');
        target.setExplicitIds(partition.enrichable);


        /**
         * ⛔⛔ TRUNCATION IS MEASURED ON WHAT THE SERVER RETURNED, NOT ON THE ENRICHABLE SUBSET.
         * `partition.enrichable.length` is always <= the cap once feed-only rows are removed, so
         * testing it would make the cap warning DISAPPEAR on exactly the sites that hit the cap AND
         * have feed-only rows — Motocard being both. Excluding rows must not be able to mask a
         * truncated response: the two facts are independent and are reported independently.
         */
        const possiblyTruncated = data.items.length >= SELECT_ALL_CAP;
        const excludedNote = describeExcluded(partition);

        // ⇒ Complete only if NOTHING was left out for either reason. Two independent ways to be
        // incomplete, so both must be false — an OR here would call a truncated result complete
        // whenever nothing happened to be excluded.
        setSelectAllSnapshot({
          // ⇒ Fingerprinted from the ids we are ABOUT TO SELECT, and stamped with the view they were
          // fetched for. Both must still hold for the badge to speak.
          fingerprint: fingerprintSelection(partition.enrichable),
          filterKey: requestKey,
          complete: !possiblyTruncated && partition.excluded.length === 0,
        });

        const notes: string[] = [];
        if (possiblyTruncated) {
          notes.push(
            `This action can select at most ${SELECT_ALL_CAP.toLocaleString()} matching products at `
            + 'once and that limit was reached, so there may be MORE that are not included. Narrow '
            + 'the filter and repeat to cover the rest.'
          );
        }
        if (excludedNote) notes.push(excludedNote);

        toast({
          // ⇒ The count is the ENRICHABLE count — the number that will actually run. Reporting the
          // raw total and then queueing a subset is the silent narrowing this replaces.
          title: notes.length > 0 ? 'Selected — read this before you run it' : 'All Products Selected',
          description: `Selected ${partition.enrichable.length.toLocaleString()} product(s) that can be enriched.`
            + (notes.length > 0 ? ` ${notes.join(' ')}` : ' They are all of the products matching this filter.'),
          ...(notes.length > 0 ? { variant: 'destructive' as const } : {}),
        });
      }
    } catch (error) {
      console.error('Error fetching all product IDs:', error);
      toast({
        title: 'Error',
        description: 'Failed to select all products.',
        variant: 'destructive',
      });
    } finally {
      selectAllInFlightRef.current = false;
      // ⚠️ A SUPERSEDED REQUEST MUST NOT CLEAR THE LOADING STATE. An older completion otherwise
      // re-enables the control while a newer request is still running — the identical defect the
      // picker's load() had, in the bulk path, found by the same reviewer twice.
      if (seq === selectAllSeq.current) setLoadingAllProductIds(false);
    }
  };

  // Clear all selections
  const clearSelection = () => {
    target.clear();
    setSelectAllSnapshot(null);
  };

  // Handle batch enrichment of selected products
  // Uses OpenAI Batch API for >20 products (50% cost savings, async processing)
  // Uses sync API for ≤20 products (immediate results)
  const handleBatchEnrichSelected = async () => {
    if (target.isEmpty || batchEnriching) return;

    setBatchEnriching(true);
    const productIds = target.selectedIds;

    // ⛔⛔ ONE PATH, ALWAYS (S1, task-8ahq0c6onh). This used to fork on a CLIENT-SIDE
    // threshold of 20: above it, /batch-enrich-async (which honours forceReEnrich); below
    // it, a loop over POST /enrich/:productId with a body of { siteId } alone. That route
    // reads no force option, and ProductEnrichmentService.enrichProduct has no cache check
    // and no already-enriched early return — so BELOW THE THRESHOLD THE FORCE CHECKBOX WAS
    // INERT IN BOTH DIRECTIONS. Ticking it changed nothing (that path already re-enriched
    // everything); leaving it OFF also changed nothing, so an operator who declined the
    // re-run paid for it anyway. Same button, same selection, opposite meaning either side
    // of 20.
    //
    // 🔑 THE ROUTE ALREADY MAKES THIS DECISION AND MAKES IT BETTER: it forks internally on
    // the same count, KEEPS IMMEDIATE RESULTS for small selections (mode:'sync', with
    // per-product statuses), and honours forceReEnrich on BOTH sides of its own fork. The
    // client fork was a second, worse copy of an existing decision, so it is DELETED rather
    // than taught the same lesson — removing the decision, not improving it.
    try {
      toast({
        title: 'Starting Enrichment',
        description: `Submitting ${productIds.length} product(s)...`,
      });

      const response = await authFetch(`${API_URL}/product-enrichment/batch-enrich-async`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          siteId: selectedSite?.id,
          productIds: productIds,  // Pass selected product IDs directly
          forceReEnrich: forceReEnrich,  // Skip cache check if true
        }),
      });

      if (response.ok) {
        const result = await response.json();
        // #3909: the batch route (createEnrichmentBatch fails CLOSED on semantic
        // gaps) can return a NON-queued outcome at a 2xx status. Check PARTIAL FIRST
        // (iter-7 MINOR): a partial result carries success:false (chunking 207) or
        // mode:'batch' + partial:true (single batch with some products blocked) — if
        // we tested success===false first, the partial branch would be unreachable.
        if (result.mode === 'sync') {
          // ⚠️ THIS BRANCH MUST COME BEFORE THE GENERIC ELSE, and its absence was a real
          // defect the moment small selections started arriving here: a mode:'sync'
          // response has no `total_products` and no `jobId`, so it fell through to
          // "Batch Job Queued" and rendered "undefined products queued for async
          // enrichment" for work that had ALREADY FINISHED synchronously.
          const outcome = computeSyncEnrichOutcome(result);
          if (outcome.selection.action === 'narrow') {
            applySelection(outcome.selection.ids);
          } else if (outcome.selection.action === 'clear') {
            applySelection([]);
          }
          toast({
            title: outcome.title,
            description: outcome.description,
            ...(outcome.variant ? { variant: outcome.variant } : {}),
          });
          if (outcome.refresh) {
            await loadOfferingData();
            await loadCategoryTree();
          }
        } else if (result.mode === 'partial' || result.partial === true) {
          // A job WAS created for the survivors; the rest were either semantic-gap
          // BLOCKED, already processing (waiting_on_overlap), or already enriched (none).
          // The copy + selection decision is a pure helper (unit-tested) — #3937/#3952.
          const outcome = computePartialBatchOutcome(result);
          if (outcome.selection.action === 'narrow') {
            applySelection(outcome.selection.ids);
          } else if (outcome.selection.action === 'clear') {
            applySelection([]);
          } // 'keep' → leave the current selection untouched
          toast({
            title: outcome.title,
            description: outcome.description,
            ...(outcome.variant ? { variant: outcome.variant } : {}),
          });
        } else if (result.success === false || result.mode === 'blocked') {
          toast({
            title: 'Enrichment Blocked',
            description: result.message || result.error || 'Products blocked by unresolved semantic gaps — resolve them first.',
            variant: 'destructive',
          });
        } else if (result.mode === 'none') {
          // #3929 CR: NO job was created — every product was already enriched/cached.
          // Do NOT show "Batch Job Queued" (there is no jobId). Keep the selection so
          // the operator can decide what to do next.
          toast({
            title: 'Nothing to Enrich',
            description: result.message || 'No products needed enrichment (all already enriched or cached).',
          });
        } else if (result.mode === 'waiting_on_overlap') {
          // #3929 iter-4 CR: NO new job was created — every product is already being
          // processed in an active batch (HTTP 200, batches:0, no jobId). Do NOT show
          // "Batch Job Queued" and do NOT clear the selection; the operator retries
          // once the in-flight batches complete.
          toast({
            title: 'Already Processing',
            description: result.message || 'Products are already being processed in active batches — no new batch created. Retry once they complete.',
          });
        } else {
          // ⛔ NAME THE SKIP, OR "QUEUED" READS AS "ALL OF THEM" (task-8ahq0c6onh). The backend now
          // narrows the set before queuing, so total_products is what was SUBMITTED, not what the
          // operator selected — and the selection is cleared immediately below. Without this,
          // picking 30 products of which 8 were already enriched reports "22 products queued",
          // wipes the selection, and never says the other 8 were skipped.
          const skippedNote = result.skipped > 0
            ? ` ${result.skipped} already enriched and skipped — tick "Force re-enrichment" to re-run them.`
            : '';
          const description = (result.batches
            ? `${result.total_products} products split into ${result.batches} batch jobs for async enrichment.`
            : `${result.total_products} products queued for async enrichment. Est. cost (extraction only): $${result.estimated_cost?.toFixed(4) || '0.00'}. Job ID: ${result.jobId?.slice(0, 8)}...`) + skippedNote;
          toast({
            title: 'Batch Job Queued',
            description,
          });
          // Clear selection - products will be enriched asynchronously
          applySelection([]);
        }
      } else {
        // #3909 iter-5: a single-batch semantic-gap block now returns HTTP 409 with
        // { success:false, mode:'blocked', message, blocked_by_category }. Prefer the
        // actionable `message` over the generic fallback so the operator sees the
        // resolve-semantic-gaps reason (was showing 'Failed to start batch enrichment').
        // #3962 CR2 MINOR: a split batch with blocked chunks AND genuine failures returns
        // mode:'blocked_and_failed' — computeBlockedOutcome treats it as a semantic-gap
        // block too, so it no longer falls through to a bare 'Batch Job Failed'.
        const error = await response.json().catch(() => ({}));
        // ⚠️ A mode:'sync' 409 IS NOT A BATCH BLOCK. The route returns 409 with
        // success:false whenever products were attempted and NONE succeeded — which
        // includes an all-blocked small selection AND a wholly failed one. Sending it to
        // computeBlockedOutcome would print the generic 'Failed to start batch enrichment'
        // and discard the per-product results the route took care to send. The sync helper
        // reads them and names the reason.
        if (error?.mode === 'sync') {
          const outcome = computeSyncEnrichOutcome(error);
          if (outcome.selection.action === 'narrow') {
            applySelection(outcome.selection.ids);
          } else if (outcome.selection.action === 'clear') {
            applySelection([]);
          }
          toast({
            title: outcome.title,
            description: outcome.description,
            ...(outcome.variant ? { variant: outcome.variant } : {}),
          });
        } else {
          const outcome = computeBlockedOutcome(error);
          toast({
            title: outcome.title,
            description: outcome.description,
            variant: outcome.variant,
          });
        }
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to start batch enrichment',
        variant: 'destructive',
      });
    } finally {
      setBatchEnriching(false);
    }
  };

  const filteredServices = services.filter(service =>
    (service.name?.toLowerCase().includes(searchTerm.toLowerCase()) || false) ||
    (service.description?.toLowerCase().includes(searchTerm.toLowerCase()) || false)
  );

  const filteredArticles = articles.filter(article =>
    (article.title?.toLowerCase().includes(searchTerm.toLowerCase()) || false) ||
    (article.excerpt?.toLowerCase().includes(searchTerm.toLowerCase()) || false)
  );

  if (!selectedSite) {
    return (
      <div className="container mx-auto p-6">
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            Please select a site from the header to view offering information.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container mx-auto p-6 space-y-6">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-48" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">The Offering</h1>
          <p className="text-muted-foreground mt-2">
            Complete catalog of products, services, and content for {selectedSite.name}
          </p>
        </div>
        <div className="flex items-center gap-4">
          {/* Sector Selector */}
          {availableSectors.length === 0 ? (
            <Badge variant="outline" className="text-amber-600 border-amber-600">
              No sectors assigned
            </Badge>
          ) : availableSectors.length === 1 ? (
            <Badge variant="secondary" className="py-2 px-4 text-base font-medium">
              {availableSectors[0].name}
            </Badge>
          ) : (
            <Select
              value={selectedSectorId?.toString() || ''}
              onValueChange={(value) => handleSectorChange(parseInt(value, 10))}
            >
              <SelectTrigger className="h-auto py-2 px-4 border-2 rounded-full font-medium text-base">
                <SelectValue placeholder="Choose a sector..." />
              </SelectTrigger>
              <SelectContent>
                {availableSectors.map((sector) => (
                  <SelectItem key={sector.id} value={sector.id.toString()}>
                    {sector.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button
            variant="outline"
            onClick={refreshOffering}
            disabled={refreshing}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh Data
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Categories</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{categories.length}</div>
            <p className="text-xs text-muted-foreground">Product groups</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Products</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalFeedProducts.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">Total items</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Services</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{services.length}</div>
            <p className="text-xs text-muted-foreground">Offerings</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Articles</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{articles.length}</div>
            <p className="text-xs text-muted-foreground">Content pieces</p>
          </CardContent>
        </Card>
      </div>

      {/* View Controls (search is in Products tab) */}
      <div className="flex items-center gap-4 justify-end">
        <div className="flex gap-2">
          <Button
            variant={viewMode === 'grid' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('grid')}
          >
            <Grid3x3 className="h-4 w-4" />
          </Button>
          <Button
            variant={viewMode === 'list' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('list')}
          >
            <List className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-7">
          <TabsTrigger value="categories">
            <Package className="h-4 w-4 mr-2" />
            Categories
          </TabsTrigger>
          <TabsTrigger value="products">
            <ShoppingBag className="h-4 w-4 mr-2" />
            Products
          </TabsTrigger>
          <TabsTrigger value="services">
            <Briefcase className="h-4 w-4 mr-2" />
            Services
          </TabsTrigger>
          <TabsTrigger value="articles">
            <FileText className="h-4 w-4 mr-2" />
            Articles
          </TabsTrigger>
          <TabsTrigger value="analytics">
            <Sparkles className="h-4 w-4 mr-2" />
            Enrichment
          </TabsTrigger>
          <TabsTrigger value="quality">
            <BarChart3 className="h-4 w-4 mr-2" />
            Data Quality
          </TabsTrigger>
          <TabsTrigger value="personas">
            <Users className="h-4 w-4 mr-2" />
            Personas
          </TabsTrigger>
        </TabsList>

        {/* Categories Tab */}
        <TabsContent value="categories">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <CardTitle>Product Categories</CardTitle>
                  <CardDescription>
                    {availableSectors.length === 0 ? (
                      <span className="text-amber-600 dark:text-amber-400">
                        No sectors assigned to this site. Go to Sites page to assign sectors.
                      </span>
                    ) : (
                      <>Categories for <strong>{availableSectors.find(s => s.id === selectedSectorId)?.name || availableSectors[0]?.name}</strong> sector</>
                    )}
                  </CardDescription>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  {/* View Mode Buttons */}
                  <div className="flex gap-2">
                    <Button
                      variant={categoryViewMode === 'wheel' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setCategoryViewMode('wheel')}
                    >
                      <Target className="mr-2 h-4 w-4" />
                      Wheel
                    </Button>
                    <Button
                      variant={categoryViewMode === 'tree' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setCategoryViewMode('tree')}
                    >
                      <Network className="mr-2 h-4 w-4" />
                      Tree
                    </Button>
                  </div>

                  {/* Market Selector (Data source) */}
                  {enrichedMarkets.length > 0 && (
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-muted-foreground">Data:</span>
                      <TrendingUp className="h-4 w-4 text-muted-foreground" />
                      <Select value={viewMarket} onValueChange={setViewMarket}>
                        <SelectTrigger className="w-[120px] h-8 text-sm">
                          <SelectValue placeholder="Market" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="United States">United States</SelectItem>
                          {enrichedMarkets
                            .filter(m => m.market !== 'United States')
                            .map((m) => (
                              <SelectItem key={m.market_code} value={m.market}>
                                {m.market}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Translation loading indicator */}
                  {isLoadingTranslations && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Translating...</span>
                    </div>
                  )}

                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Unmapped Categories Alert */}
              {unmappedCategories.length > 0 && (
                <Alert className="mb-4 border-amber-500/50 bg-amber-50 dark:bg-amber-950/20">
                  <Sparkles className="h-4 w-4 text-amber-600" />
                  <AlertDescription>
                    <div className="space-y-4">
                      {/* Header with Map All button */}
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="font-semibold">{unmappedCategories.length} unmapped categories</span> found
                          <br />
                          <span className="text-sm text-muted-foreground">Use AI to map them to Google Product Taxonomy</span>
                        </div>
                        <Button
                          onClick={handleMapAllCategories}
                          disabled={mappingInProgress}
                          className="ml-4"
                          size="sm"
                        >
                          {mappingInProgress ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Mapping...
                            </>
                          ) : (
                            <>
                              <Sparkles className="mr-2 h-4 w-4" />
                              Map All with AI
                            </>
                          )}
                        </Button>
                      </div>

                      {/* Progress indicator during batch mapping */}
                      {mappingInProgress && mappingResults && (
                        <div className="p-3 bg-white dark:bg-gray-800 rounded border">
                          <div className="text-sm font-medium mb-2">Progress:</div>
                          <div className="text-sm space-y-1">
                            <div>✓ Mapped: {mappingResults.mapped} of {mappingResults.total}</div>
                            {mappingResults.failed > 0 && (
                              <div className="text-red-600">✗ Failed: {mappingResults.failed}</div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Individual unmapped categories list */}
                      <div className="max-h-64 overflow-y-auto space-y-2 border-t pt-3">
                        {unmappedCategories.slice(0, 10).map((category) => (
                          <div
                            key={category.name}
                            className="flex items-center justify-between p-2 bg-white dark:bg-gray-800 rounded border"
                          >
                            <div className="flex-1">
                              <span className="font-medium">{category.name}</span>
                              <span className="ml-2 text-sm text-muted-foreground">
                                ({category.product_count} products)
                              </span>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleMapSingleCategory(category.name)}
                              disabled={mappingInProgress || mappingCategory === category.name}
                            >
                              {mappingCategory === category.name ? (
                                <>
                                  <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                                  Mapping...
                                </>
                              ) : (
                                <>
                                  <Sparkles className="mr-2 h-3 w-3" />
                                  Map
                                </>
                              )}
                            </Button>
                          </div>
                        ))}
                        {unmappedCategories.length > 10 && (
                          <div className="text-sm text-center text-muted-foreground py-2">
                            ... and {unmappedCategories.length - 10} more
                          </div>
                        )}
                      </div>

                      {/* Final results after batch mapping completes */}
                      {!mappingInProgress && mappingResults && mappingResults.total > 0 && (
                        <div className="p-3 bg-green-50 dark:bg-green-950/20 rounded border border-green-200 dark:border-green-800">
                          <div className="text-sm font-medium mb-2">✓ Batch Mapping Complete!</div>
                          <div className="text-sm space-y-1">
                            <div>Successfully mapped: {mappingResults.mapped} categories</div>
                            {mappingResults.failed > 0 && (
                              <div className="text-red-600">Could not map: {mappingResults.failed} categories</div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              {/* Orphan Categories Alert - categories that need Google taxonomy classification */}
              {orphanCategories.length > 0 && (
                <Alert className="mb-4 border-red-500/50 bg-red-50 dark:bg-red-950/20">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  <AlertDescription>
                    <div className="space-y-4">
                      {/* Header with Classify All button */}
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="font-semibold text-red-800 dark:text-red-300">{orphanCategories.length} orphan categories</span> need classification
                          <br />
                          <span className="text-sm text-muted-foreground">These categories don't follow Google Product Taxonomy format</span>
                        </div>
                        <Button
                          onClick={handleClassifyAllOrphans}
                          disabled={mappingInProgress}
                          className="ml-4 bg-red-600 hover:bg-red-700"
                          size="sm"
                        >
                          {mappingInProgress ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Classifying...
                            </>
                          ) : (
                            <>
                              <Sparkles className="mr-2 h-4 w-4" />
                              Classify All with AI
                            </>
                          )}
                        </Button>
                      </div>

                      {/* Individual orphan categories list */}
                      <div className="max-h-48 overflow-y-auto space-y-2 border-t pt-3">
                        {orphanCategories.slice(0, 8).map((category) => (
                          <div
                            key={category.name}
                            className="flex items-center justify-between p-2 bg-white dark:bg-gray-800 rounded border border-red-200 dark:border-red-800"
                          >
                            <div className="flex-1">
                              <span className="font-medium text-red-800 dark:text-red-300">{category.name}</span>
                              <span className="ml-2 text-sm text-muted-foreground">
                                ({category.product_count} products)
                              </span>
                            </div>
                            <Badge variant="outline" className="text-xs border-red-400 text-red-600">
                              Needs Classification
                            </Badge>
                          </div>
                        ))}
                        {orphanCategories.length > 8 && (
                          <div className="text-sm text-center text-muted-foreground py-2">
                            ... and {orphanCategories.length - 8} more
                          </div>
                        )}
                      </div>
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              {/* Review AI Mappings Section */}
              {categoryTreeStats?.mapped_subcategories > 0 && (
                <div className="mb-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleToggleActiveMappings}
                    className="w-full justify-between"
                  >
                    <span className="flex items-center gap-2">
                      <Sparkles className="h-4 w-4" />
                      Review AI Mappings ({categoryTreeStats.mapped_subcategories} active)
                    </span>
                    {showActiveMappings ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </Button>

                  {showActiveMappings && (
                    <Card className="mt-2 border-blue-200 dark:border-blue-800">
                      <CardContent className="pt-4">
                        {loadingMappings ? (
                          <div className="flex items-center justify-center py-4">
                            <Loader2 className="h-5 w-5 animate-spin" />
                            <span className="ml-2 text-sm text-muted-foreground">Loading mappings...</span>
                          </div>
                        ) : activeMappings.length > 0 ? (
                          <div className="space-y-2 max-h-64 overflow-y-auto">
                            <div className="text-sm text-muted-foreground mb-3">
                              These subcategories were mapped to Google Product Taxonomy categories using AI.
                              Mark any incorrect mappings as excluded.
                            </div>
                            {activeMappings.map((mapping) => (
                              <div
                                key={mapping.id}
                                className="flex items-center justify-between p-2 bg-white dark:bg-gray-800 rounded border"
                              >
                                <div className="flex-1">
                                  <span className="font-medium">{mapping.subcategory_name}</span>
                                  <span className="mx-2 text-muted-foreground">→</span>
                                  <span className="text-sm text-blue-600 dark:text-blue-400">
                                    {mapping.google_parent_path?.split(' > ').pop()}
                                  </span>
                                  <span className="ml-2 text-xs text-muted-foreground">
                                    ({mapping.product_count} products)
                                  </span>
                                </div>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleExcludeCategory(mapping.subcategory_name, true, 'Incorrect AI mapping')}
                                  disabled={excludingCategory === mapping.subcategory_name}
                                  className="border-red-300 text-red-600 hover:bg-red-50"
                                >
                                  {excludingCategory === mapping.subcategory_name ? (
                                    <>
                                      <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                                      Excluding...
                                    </>
                                  ) : (
                                    'Exclude'
                                  )}
                                </Button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-sm text-muted-foreground text-center py-4">
                            No active mappings found
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}

              {/* Excluded Mappings Alert */}
              {excludedCategories.length > 0 && (
                <Alert className="mb-4 border-red-500/50 bg-red-50 dark:bg-red-950/20">
                  <Info className="h-4 w-4 text-red-600" />
                  <AlertDescription>
                    <div className="space-y-4">
                      {/* Header */}
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="font-semibold text-red-700 dark:text-red-400">
                            {excludedCategories.length} excluded mappings
                          </span>
                          <br />
                          <span className="text-sm text-muted-foreground">
                            These AI mappings were marked as incorrect and are not used in question generation
                          </span>
                        </div>
                      </div>

                      {/* Excluded categories list */}
                      <div className="max-h-48 overflow-y-auto space-y-2 border-t pt-3">
                        {excludedCategories.map((category) => (
                          <div
                            key={category.name}
                            className="flex items-center justify-between p-2 bg-white dark:bg-gray-800 rounded border border-red-200 dark:border-red-800"
                          >
                            <div className="flex-1">
                              <span className="font-medium text-red-700 dark:text-red-400 line-through">
                                {category.name}
                              </span>
                              <span className="ml-2 text-xs text-muted-foreground">
                                was mapped to: {category.google_parent_path?.split(' > ').pop()}
                              </span>
                              {category.exclusion_reason && (
                                <div className="text-xs text-red-600 dark:text-red-400">
                                  Reason: {category.exclusion_reason}
                                </div>
                              )}
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleExcludeCategory(category.name, false)}
                              disabled={excludingCategory === category.name}
                              className="border-green-500 text-green-600 hover:bg-green-50"
                            >
                              {excludingCategory === category.name ? (
                                <>
                                  <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                                  Restoring...
                                </>
                              ) : (
                                <>
                                  <RefreshCw className="mr-2 h-3 w-3" />
                                  Re-enable
                                </>
                              )}
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              {loadingCategoryTree ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <span className="ml-3 text-muted-foreground">Loading categories...</span>
                </div>
              ) : categoryTreeData.length > 0 ? (
                <>
                  {/* Breadcrumb Navigation for Wheel View */}
                  {categoryViewMode === 'wheel' && categoryBreadcrumb.length > 0 && (
                    <div className="flex items-center gap-2 mb-4 p-3 bg-muted rounded-lg">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleBreadcrumbClick(-1)}
                        className="h-8 px-2"
                      >
                        <Home className="h-4 w-4" />
                      </Button>
                      {categoryBreadcrumb.map((bc, index) => (
                        <div key={bc.category_path} className="flex items-center gap-2">
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleBreadcrumbClick(index)}
                            className="h-8"
                          >
                            {bc.category_name}
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Wheel View with Action Buttons on the right */}
                  {categoryViewMode === 'wheel' && (
                    <div className="flex items-start gap-6">
                      {/* Wheel and Legend - takes most of the space */}
                      <div className="flex-1">
                        <DemandWheel
                          data={(categoryTreeData || []).map((cat: any) => ({
                            id: cat.id,
                            sector_category_id: cat.sector_category_id,
                            category_name: cat.category_name,
                            display_name: categoryTranslations[cat.sector_category_id] || cat.category_name,
                            category_path: cat.category_path,
                            relative_demand_score: cat.relative_demand_score || cat.avg_monthly_searches || 0,
                            avg_monthly_searches: cat.avg_monthly_searches || 0,
                            subcategory_count: cat.subcategory_count || 0,
                            source_type: cat.source_type,
                            product_count: cat.product_count || 0,
                            enriched_count: cat.enriched_count || 0,
                            site_subcategories: cat.site_subcategories || [],
                          }))}
                          onCategoryClick={handleCategoryClick}
                          centerLabel={
                            currentCategoryView
                              ? (categoryTranslations[currentCategoryView.sector_category_id] || currentCategoryView.category_name)
                              : (selectedSite?.domain || 'Product Categories')
                          }
                          centerScore={(categoryTreeData || []).reduce((sum: number, cat: any) => sum + (cat.avg_monthly_searches || 0), 0)}
                          scoreLabel="Demand Score"
                          showProductRing={true}
                        />
                        {/* Legend for the wheel */}
                        <div className="flex justify-center gap-6 mt-4 text-sm text-muted-foreground">
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 rounded bg-green-500 opacity-70" />
                            <span>Inner ring: Market demand (searches/mo)</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 rounded bg-primary opacity-60" />
                            <span>Outer ring: Your products</span>
                          </div>
                        </div>
                      </div>

                      {/* Action Buttons Panel - on the right, vertically centered */}
                      {currentCategoryView && (currentLevelInfo?.product_count > 0 || currentLevelInfo?.sector_question_wheel || currentLevelInfo?.site_question_wheel) && (
                        <div className="flex flex-col gap-3 min-w-[200px] self-center">
                          {/* View Products at this level */}
                          {currentLevelInfo?.product_count > 0 && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleProductCountClick(currentCategoryView.category_name)}
                              className="gap-2 justify-start"
                            >
                              <Eye className="h-4 w-4" />
                              <span className="truncate">View Products</span>
                              <Badge variant="secondary" className="ml-auto">
                                {currentLevelInfo.product_count.toLocaleString()}
                              </Badge>
                            </Button>
                          )}

                          {/* AI Enrichment Stats (informational only - enrichment is done in Products tab) */}
                          {currentLevelInfo?.product_count > 0 && (
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <Sparkles className={`h-3.5 w-3.5 ${
                                currentLevelInfo.enriched_count === currentLevelInfo.product_count
                                  ? 'text-green-500'
                                  : currentLevelInfo.enriched_count > 0
                                  ? 'text-amber-500'
                                  : 'text-muted-foreground'
                              }`} />
                              <span>AI Enriched:</span>
                              <span className={`font-medium ${
                                currentLevelInfo.enriched_count === currentLevelInfo.product_count
                                  ? 'text-green-600'
                                  : currentLevelInfo.enriched_count > 0
                                  ? 'text-amber-600'
                                  : ''
                              }`}>
                                {currentLevelInfo.enriched_count}/{currentLevelInfo.product_count}
                                {' '}({Math.round((currentLevelInfo.enriched_count / currentLevelInfo.product_count) * 100)}%)
                              </span>
                            </div>
                          )}

                          {/* Sector Question Wheel link */}
                          {currentLevelInfo?.sector_question_wheel && selectedSectorId && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                if (currentLevelInfo.sector_question_wheel && selectedSectorId) {
                                  navigate(`/sectors/${selectedSectorId}/question-wheels/${currentLevelInfo.sector_question_wheel.id}/debug`);
                                }
                              }}
                              className="gap-2 justify-start"
                            >
                              <HelpCircle className="h-4 w-4" />
                              <span className="truncate">Sector Questions</span>
                              <Badge variant="secondary" className="ml-auto">
                                {currentLevelInfo.sector_question_wheel.question_count}
                              </Badge>
                            </Button>
                          )}

                          {/* Site Question Wheel link */}
                          {currentLevelInfo?.site_question_wheel && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                if (currentLevelInfo.site_question_wheel) {
                                  navigate(`/question-wheels/${currentLevelInfo.site_question_wheel.id}/debug`);
                                }
                              }}
                              className="gap-2 justify-start"
                            >
                              <HelpCircle className="h-4 w-4 text-primary" />
                              <span className="truncate">Site Questions</span>
                              <Badge variant="secondary" className="ml-auto">
                                {currentLevelInfo.site_question_wheel.question_count}
                              </Badge>
                            </Button>
                          )}

                          {/* AEO Readiness Check */}
                          {currentLevelInfo?.category_path && selectedSite?.id && (
                            <AEOReadinessButton
                              siteId={selectedSite.id}
                              categoryPath={currentLevelInfo.category_path}
                              variant="outline"
                              size="sm"
                              className="gap-2 justify-start"
                            />
                          )}

                          {/* AEO Content Generation */}
                          {currentLevelInfo?.category_path && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleGenerateCategoryAEO(currentLevelInfo.category_path!)}
                              disabled={generatingCategoryAEO}
                              className="gap-2 justify-start bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-950/30 dark:to-blue-950/30 border-purple-200 dark:border-purple-800 hover:from-purple-100 hover:to-blue-100 dark:hover:from-purple-950/50 dark:hover:to-blue-950/50"
                            >
                              {generatingCategoryAEO ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Zap className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                              )}
                              <span className="truncate text-purple-700 dark:text-purple-300">
                                {generatingCategoryAEO ? 'Generating...' : 'AEO Content'}
                              </span>
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Tree View */}
                  {categoryViewMode === 'tree' && (
                    <div className="space-y-2">
                      {(categoryTreeData || []).map((category: any) => (
                        <TreeNode
                          key={category.id}
                          category={category}
                          onProductCountClick={handleProductCountClick}
                          onCategoryClick={handleLeafCategoryClick}
                          siteId={selectedSite?.id}
                        />
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    No categories found. Categories are extracted from the Google Product Category field in your product feed.
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Products Tab */}
        <TabsContent value="products">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Product Catalog</CardTitle>
                  <CardDescription>
                    Complete listing of products found on your website
                  </CardDescription>
                </div>
                {selectedCategory && (
                  <Badge
                    variant="secondary"
                    className="cursor-pointer"
                    onClick={() => setSelectedCategory(null)}
                  >
                    Category: {selectedCategory} ✕
                  </Badge>
                )}
                {selectedSubcategory && (
                  <Badge
                    variant="secondary"
                    className="cursor-pointer"
                    onClick={() => setSelectedSubcategory(null)}
                  >
                    Subcategory: {selectedSubcategory} ✕
                  </Badge>
                )}
                {selectedCategory && selectedSite?.id && (
                  <AEOReadinessButton
                    siteId={selectedSite.id}
                    categoryPath={selectedCategory}
                    variant="outline"
                    size="sm"
                  />
                )}
              </div>
            </CardHeader>
            <CardContent>
              {/* Search and Filter Bar */}
              <div className="flex gap-4 mb-6">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  {/* ⛔ THE PLACEHOLDER IS THE ONLY PLACE THIS CAPABILITY IS ANNOUNCED. An operator
                      who does not know the box accepts a SKU will keep typing names and keep getting
                      the ambiguous set — the search would be fixed and unused. */}
                  <Input
                    placeholder="Search by product name or SKU..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    className="pl-10"
                  />
                </div>
                {/* Enrichment Filter */}
                <Select
                  value={enrichedFilter}
                  onValueChange={(value: 'all' | 'enriched' | 'unenriched') => {
                    setEnrichedFilter(value);
                    setCurrentPage(1);
                  }}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All products</SelectItem>
                    <SelectItem value="enriched">Enriched only</SelectItem>
                    <SelectItem value="unenriched">Not enriched</SelectItem>
                  </SelectContent>
                </Select>
                {/* AEO Content Filter */}
                <Select
                  value={aeoFilter}
                  onValueChange={(value: 'all' | 'generated' | 'not_generated') => {
                    setAeoFilter(value);
                    setCurrentPage(1);
                  }}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Filter by AEO" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All AEO status</SelectItem>
                    <SelectItem value="generated">AEO generated</SelectItem>
                    <SelectItem value="not_generated">AEO not generated</SelectItem>
                  </SelectContent>
                </Select>
                {/* ⛔⛔ THE PICKER'S ENTRY POINT LIVES HERE, IN THE ALWAYS-VISIBLE FILTER BAR, and
                    deliberately NOT in the Selection Toolbar below — that toolbar is gated on
                    `filteredProducts.length > 0`, so it disappears exactly when a search returns
                    nothing, which is precisely the moment an operator needs to paste a list or look
                    a SKU up. A button that vanishes when you need it is not an affordance.
                    (task-dx7eahgnvt: the picker existed with ZERO production importers, so it was
                    tree-shaken out of the bundle entirely — tested code, not an operator
                    capability, under the standing rule that a capability with no button is not
                    delivered.) */}
                {selectedSite?.id && (
                  <Button
                    variant="outline"
                    onClick={() => setPickerOpen(true)}
                    className="gap-2 shrink-0"
                  >
                    <ListChecks className="h-4 w-4" />
                    Find or paste products
                  </Button>
                )}
                {pagination && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Package className="h-4 w-4" />
                    <span>
                      Showing {((currentPage - 1) * 50) + 1}-{Math.min(currentPage * 50, pagination.total)} of {pagination.total}
                    </span>
                  </div>
                )}
              </div>

              {/* Selection Toolbar */}
              {filteredProducts.length > 0 && (
                <div className="flex flex-wrap items-center gap-4 mb-4 p-3 bg-muted/50 rounded-lg">
                  {/* Select All on page button - show when not all visible are selected */}
                  {!allVisibleSelected && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={selectAllProducts}
                      className="gap-2"
                    >
                      <Square className="h-4 w-4" />
                      Select All on Page
                    </Button>
                  )}

                  {/* Select ALL products across pages - show when all visible are selected but more exist */}
                  {allVisibleSelected && !selectionMatchesSelectAll && totalProductsCount > filteredProducts.length && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={selectAllProductsAcrossPages}
                      disabled={loadingAllProductIds}
                      className="gap-2 border-primary text-primary hover:bg-primary/10"
                    >
                      {loadingAllProductIds ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Loading...
                        </>
                      ) : (
                        <>
                          <CheckSquare className="h-4 w-4" />
                          Select All {totalProductsCount} Products
                        </>
                      )}
                    </Button>
                  )}

                  {/* Clear Selection button - show when any are selected */}
                  {target.selectedIds.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={clearSelection}
                      className="gap-2"
                    >
                      <Square className="h-4 w-4" />
                      Clear Selection
                    </Button>
                  )}

                  {target.selectedIds.length > 0 && (
                    <>
                      <span className="text-sm text-muted-foreground">
                        {target.selectedIds.length} selected
                        {selectionMatchesSelectAll && (
                          // ⇒ The badge tells the truth for as long as it is on screen. "(all
                          // products)" is claimed ONLY when nothing was excluded and the cap was not
                          // reached; otherwise it says what is actually true and stays weaker.
                          selectionIsComplete
                            ? <span className="font-medium text-primary"> (all products)</span>
                            : <span className="font-medium text-destructive"> (all ELIGIBLE products — some were excluded or not loaded)</span>
                        )}
                      </span>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="force-re-enrich"
                          checked={forceReEnrich}
                          onCheckedChange={(checked) => setForceReEnrich(checked === true)}
                        />
                        <label
                          htmlFor="force-re-enrich"
                          className="text-sm text-muted-foreground cursor-pointer"
                        >
                          Force re-enrich
                        </label>
                      </div>
                      <Button
                        variant="default"
                        size="sm"
                        onClick={handleBatchEnrichSelected}
                        disabled={batchEnriching}
                        className="gap-2"
                      >
                        {batchEnriching ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Enriching...
                          </>
                        ) : (
                          <>
                            <Sparkles className="h-4 w-4" />
                            Enrich Selected ({target.selectedIds.length})
                          </>
                        )}
                      </Button>
                    </>
                  )}
                </div>
              )}

              {filteredProducts.length > 0 ? (
                <>
                <div className={viewMode === 'grid' ?
                  "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" :
                  "space-y-4"
                }>
                  {filteredProducts.map((product) => (
                    <Card key={product.id} className={`overflow-hidden ${selectedIdSet.has(product.id) ? 'ring-2 ring-primary' : ''}`}>
                      <div className={viewMode === 'grid' ? '' : 'flex gap-4'}>
                        {product.image_url && (
                          <div className={viewMode === 'grid' ? 'h-48 relative' : 'w-32 h-32 flex-shrink-0 relative'}>
                            {/* Selection Checkbox on Image */}
                            <div className="absolute top-2 left-2 z-10">
                              <Checkbox
                                checked={selectedIdSet.has(product.id)}
                                onCheckedChange={() => toggleProductSelection(product)}
                                disabled={product.id_kind !== 'kbe'}
                                title={product.id_kind !== 'kbe'
                                  ? 'Not in the knowledge base yet — cannot be enriched until its feed is ingested'
                                  : undefined}
                                className="h-5 w-5 bg-white/90 border-2"
                              />
                            </div>
                            <img
                              src={product.image_url}
                              alt={product.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                        <div className="flex-1">
                          <CardHeader>
                            <div className="flex items-start justify-between gap-2">
                              {/* Checkbox for products without images */}
                              {!product.image_url && (
                                <Checkbox
                                  checked={selectedIdSet.has(product.id)}
                                  onCheckedChange={() => toggleProductSelection(product)}
                                disabled={product.id_kind !== 'kbe'}
                                title={product.id_kind !== 'kbe'
                                  ? 'Not in the knowledge base yet — cannot be enriched until its feed is ingested'
                                  : undefined}
                                  className="h-5 w-5 mt-1 flex-shrink-0"
                                />
                              )}
                              <div className="flex-1">
                                {product.url ? (
                                  <a
                                    href={cleanProductUrl(product.url)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="hover:underline"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <CardTitle className="text-base flex items-center gap-1 hover:text-primary">
                                      {product.name}
                                      <ExternalLink className="h-3 w-3 opacity-50" />
                                    </CardTitle>
                                  </a>
                                ) : (
                                  <CardTitle className="text-base">{product.name}</CardTitle>
                                )}
                                {/* ⛔⛔ THE SKU, ON THE CARD, SELECTABLE (task-epvtogyjl8).
                                    THE NAME IS NOT AN IDENTIFIER. One BikeInn name covers THREE
                                    distinct products, so a card showing only the name cannot tell an
                                    operator which row they have selected — and this screen's whole
                                    purpose is to select exact products for enrichment.
                                    ⇒ Rendered as `select-text font-mono` deliberately: the operator's
                                    next action is to COPY this value into a search or a job, and a
                                    truncated or unselectable identifier is the same as an absent one.
                                    ⚠️ NOT hidden when absent-but-falsy vs missing: `sku` is null only
                                    on rows that genuinely have no feed SKU (knowledge-base-only
                                    entries), and those render nothing rather than an empty chip that
                                    would read as "SKU: " with a blank value. */}
                                {product.sku && (
                                  <div className="mt-1 text-xs font-mono text-muted-foreground select-text break-all">
                                    SKU {product.sku}
                                  </div>
                                )}
                                <div className="flex flex-wrap gap-1 mt-1">
                                  <Badge variant="outline">
                                    {product.category}
                                  </Badge>
                                  {product.is_enriched && (
                                    <Badge variant="secondary" className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                      <CheckCircle2 className="h-3 w-3 mr-1" />
                                      Enriched
                                    </Badge>
                                  )}
                                  {product.aeo_content_error && (
                                    <span role="alert" className="text-sm text-destructive">
                                      AEO content unavailable: {product.aeo_content_error}
                                    </span>
                                  )}
                                  {product.has_aeo_content && (
                                    <Badge variant="secondary" className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                      <Zap className="h-3 w-3 mr-1" />
                                      AEO Ready
                                    </Badge>
                                  )}
                                </div>
                              </div>
                              {product.price && (
                                <span className="flex items-center text-lg font-bold">
                                  <DollarSign className="h-4 w-4" />
                                  {product.price}
                                </span>
                              )}
                            </div>
                          </CardHeader>
                          <CardContent>
                            <p className="text-sm text-muted-foreground line-clamp-2">
                              {product.description}
                            </p>

                            {product.rating && (
                              <div className="flex items-center gap-1 mt-2">
                                {[...Array(5)].map((_, i) => (
                                  <Star
                                    key={i}
                                    className={`h-3 w-3 ${
                                      i < Math.floor(product.rating!) ?
                                      'fill-yellow-400 text-yellow-400' :
                                      'text-gray-300'
                                    }`}
                                  />
                                ))}
                                <span className="text-xs text-muted-foreground ml-1">
                                  ({product.rating})
                                </span>
                              </div>
                            )}

                            {product.features && product.features.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {product.features.slice(0, 3).map((feature, idx) => (
                                  <Badge key={idx} variant="secondary" className="text-xs">
                                    {typeof feature === 'string' ? feature : (feature?.value || String(feature))}
                                  </Badge>
                                ))}
                              </div>
                            )}

                            <div className="flex items-center justify-between mt-4">
                              {product.in_stock !== undefined && (
                                <Badge variant={product.in_stock ? "success" : "destructive"}>
                                  {product.in_stock ? "In Stock" : "Out of Stock"}
                                </Badge>
                              )}
                              <div className="flex gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleEnrichProduct(product.id, product.id_kind)}
                                  disabled={enrichingProduct === product.id || product.id_kind !== 'kbe'}
                                  title={product.id_kind !== 'kbe'
                                    ? 'Not in the knowledge base yet — cannot be enriched until its feed is ingested'
                                    : undefined}
                                >
                                  {enrichingProduct === product.id ? (
                                    <>
                                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                      Enriching...
                                    </>
                                  ) : (
                                    <>
                                      <Sparkles className="h-3 w-3 mr-1" />
                                      AI Enrich
                                    </>
                                  )}
                                </Button>
                                <Button
                                  variant={product.has_aeo_content ? "default" : "outline"}
                                  size="sm"
                                  onClick={() => product.has_aeo_content
                                    ? handleGenerateAEOContent(product)
                                    : setAeoConfirmProduct(product)
                                  }
                                  disabled={!!product.aeo_content_error || (!product.has_aeo_content && !product.is_enriched) || generatingAEO === (product.product_feed_id || product.id)}
                                  title={product.aeo_content_error || (product.has_aeo_content ? "View AEO content" : !product.is_enriched ? "Enrich product first" : "Trigger AEO generation")}
                                  className={!product.has_aeo_content && product.is_enriched ? "text-gray-600 border-gray-300 dark:text-gray-400 dark:border-gray-600" : ""}
                                >
                                  {generatingAEO === (product.product_feed_id || product.id) ? (
                                    <>
                                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                      Generating...
                                    </>
                                  ) : (
                                    <>
                                      <Zap className="h-3 w-3 mr-1" />
                                      AEO Content
                                    </>
                                  )}
                                </Button>
                                <Button
                                  variant="outline"
                                  size="icon"
                                  className="h-8 w-8 border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-950"
                                  onClick={() => navigate(`/research/competitive-benchmark?sourceType=product&sourceId=${product.id}&sourceName=${encodeURIComponent(product.name)}`)}
                                  title="Benchmark"
                                >
                                  <BarChart3 className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="icon"
                                  className="h-8 w-8 border-green-300 dark:border-green-700 text-green-700 dark:text-green-300 hover:bg-green-50 dark:hover:bg-green-950"
                                  onClick={() => setVisibilityProduct({ id: product.id, name: product.name, url: product.url })}
                                  title="SEO Visibility"
                                >
                                  <TrendingUp className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="icon"
                                  className="h-8 w-8 border-purple-300 dark:border-purple-700 text-purple-700 dark:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-950"
                                  onClick={() => navigate(`/success-story/${product.id}`)}
                                  title="Success Story"
                                >
                                  <Trophy className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
                                  <a href={cleanProductUrl(product.url)} target="_blank" rel="noopener noreferrer" title="View on site">
                                    <ExternalLink className="h-4 w-4" />
                                  </a>
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>

                {/* Pagination Controls */}
                {pagination && pagination.pages > 1 && (
                  <div className="flex items-center justify-between mt-6 pt-6 border-t">
                    <Button
                      variant="outline"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>

                    <div className="flex items-center gap-2">
                      {/* Show first page */}
                      {currentPage > 3 && (
                        <>
                          <Button
                            variant={1 === currentPage ? "default" : "outline"}
                            size="sm"
                            onClick={() => handlePageChange(1)}
                          >
                            1
                          </Button>
                          {currentPage > 4 && <span className="text-muted-foreground">...</span>}
                        </>
                      )}

                      {/* Show pages around current page */}
                      {Array.from({ length: pagination.pages }, (_, i) => i + 1)
                        .filter(page =>
                          page === currentPage ||
                          page === currentPage - 1 ||
                          page === currentPage - 2 ||
                          page === currentPage + 1 ||
                          page === currentPage + 2
                        )
                        .map(page => (
                          <Button
                            key={page}
                            variant={page === currentPage ? "default" : "outline"}
                            size="sm"
                            onClick={() => handlePageChange(page)}
                          >
                            {page}
                          </Button>
                        ))}

                      {/* Show last page */}
                      {currentPage < pagination.pages - 2 && (
                        <>
                          {currentPage < pagination.pages - 3 && <span className="text-muted-foreground">...</span>}
                          <Button
                            variant={pagination.pages === currentPage ? "default" : "outline"}
                            size="sm"
                            onClick={() => handlePageChange(pagination.pages)}
                          >
                            {pagination.pages}
                          </Button>
                        </>
                      )}
                    </div>

                    <Button
                      variant="outline"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === pagination.pages}
                    >
                      Next
                    </Button>
                  </div>
                )}
                </>
              ) : (
                /* ⛔⛔ "NO MATCHES" AND "THE QUERY FAILED" ARE DIFFERENT FACTS AND MUST READ
                   DIFFERENTLY (task-djasurv8m7). Clearing a stale list on failure is only half the
                   fix: an empty list rendered through the message below would assert that nothing
                   matched, which is a claim the request never actually returned. That is the same
                   silent-widening shape one step over — the operator still can't tell that their
                   filter did not run. */
                listingError ? (
                  <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      The product list could not be loaded ({listingError}). This is <strong>not</strong> a
                      result for your search — nothing was filtered. Retry, and if it persists report the
                      error above rather than treating the catalogue as empty.
                    </AlertDescription>
                  </Alert>
                ) : (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    {searchTerm || selectedCategory || selectedSubcategory ?
                      'No products match your filters.' :
                      'No products found. Products will be extracted from your website automatically.'}
                  </AlertDescription>
                </Alert>
                )
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Services Tab */}
        <TabsContent value="services">
          <Card>
            <CardHeader>
              <CardTitle>Services Offered</CardTitle>
              <CardDescription>
                Professional services and offerings available on your website
              </CardDescription>
            </CardHeader>
            <CardContent>
              {filteredServices.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {filteredServices.map((service) => (
                    <Card key={service.id}>
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <CardTitle className="text-base">{service.name}</CardTitle>
                          {service.price_range && (
                            <Badge variant="secondary">
                              {service.price_range}
                            </Badge>
                          )}
                        </div>
                        {service.category && (
                          <Badge variant="outline" className="w-fit">
                            {service.category}
                          </Badge>
                        )}
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground">
                          {service.description}
                        </p>
                        {service.duration && (
                          <div className="flex items-center gap-2 mt-3 text-sm">
                            <Tag className="h-3 w-3" />
                            <span>Duration: {service.duration}</span>
                          </div>
                        )}
                        <Button variant="link" size="sm" className="px-0 mt-3" asChild>
                          <a href={service.url} target="_blank" rel="noopener noreferrer">
                            Learn more →
                          </a>
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    {searchTerm ?
                      'No services match your search.' :
                      'No services found. Services will be extracted from your website if available.'}
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Articles Tab */}
        <TabsContent value="articles">
          <Card>
            <CardHeader>
              <CardTitle>Knowledge Articles</CardTitle>
              <CardDescription>
                Blog posts and articles that establish authority and share expertise
              </CardDescription>
            </CardHeader>
            <CardContent>
              {filteredArticles.length > 0 ? (
                <div className="space-y-4">
                  {filteredArticles.map((article) => (
                    <Card key={article.id} className="hover:shadow-md transition-shadow">
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <CardTitle className="text-base">
                              {article.title}
                            </CardTitle>
                            <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                              {article.author && (
                                <span>By {article.author}</span>
                              )}
                              <span>{new Date(article.published_date).toLocaleDateString()}</span>
                              {article.read_time && (
                                <span>{article.read_time} read</span>
                              )}
                              {article.views && (
                                <Badge variant="outline" className="text-xs">
                                  {article.views} views
                                </Badge>
                              )}
                            </div>
                          </div>
                          {article.category && (
                            <Badge variant="secondary">
                              {article.category}
                            </Badge>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground">
                          {article.excerpt}
                        </p>
                        <Button variant="link" size="sm" className="px-0 mt-3" asChild>
                          <a href={article.url} target="_blank" rel="noopener noreferrer">
                            Read full article →
                          </a>
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    {searchTerm ?
                      'No articles match your search.' :
                      'No articles found. Articles and blog posts will be extracted from your website if available.'}
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Enrichment Analytics Tab */}
        <TabsContent value="analytics">
          <Card>
            <CardHeader>
              <CardTitle>Product Enrichment Analytics</CardTitle>
              <CardDescription>
                Measure the impact of AI-powered product enrichment on your catalog
              </CardDescription>
            </CardHeader>
            <CardContent>
              {selectedSite?.id ? (
                <ProductEnrichmentDashboard siteId={selectedSite.id} />
              ) : (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription>Please select a site to view enrichment analytics.</AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Data Quality Tab */}
        <TabsContent value="quality">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Data Quality Audit
              </CardTitle>
              <CardDescription>
                Category-level view of AI enrichment coverage and review status across your product catalog
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Quick Stats */}
              {loadingDataQuality ? (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {[1, 2, 3, 4].map((i) => (
                    <Card key={i} className="bg-muted/30">
                      <CardContent className="pt-4">
                        <Skeleton className="h-4 w-20 mb-2" />
                        <Skeleton className="h-8 w-16" />
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card className="bg-muted/30">
                    <CardContent className="pt-4">
                      <div className="text-sm text-muted-foreground">Total Products</div>
                      <div className="text-2xl font-bold">
                        {(dataQualityStats?.total_products || 0).toLocaleString()}
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-green-50 dark:bg-green-950/30">
                    <CardContent className="pt-4">
                      <div className="text-sm text-green-700 dark:text-green-400">AI Enriched</div>
                      <div className="text-2xl font-bold text-green-700 dark:text-green-400">
                        {(dataQualityStats?.total_enriched || 0).toLocaleString()}
                      </div>
                      {dataQualityStats && dataQualityStats.total_products > 0 && (
                        <div className="text-xs text-green-600 dark:text-green-500">
                          {dataQualityStats.overall_enrichment_pct}% coverage
                        </div>
                      )}
                    </CardContent>
                  </Card>
                  <Card className="bg-amber-50 dark:bg-amber-950/30">
                    <CardContent className="pt-4">
                      <div className="text-sm text-amber-700 dark:text-amber-400">Needs Review</div>
                      <div className="text-2xl font-bold text-amber-700 dark:text-amber-400">
                        {(dataQualityStats?.total_needs_review || 0).toLocaleString()}
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-gray-50 dark:bg-gray-950/30">
                    <CardContent className="pt-4">
                      <div className="text-sm text-muted-foreground">Categories</div>
                      <div className="text-2xl font-bold">
                        {dataQualityStats?.category_count || 0}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Action Button */}
              <div className="flex justify-center py-8">
                <Button
                  size="lg"
                  className="gap-2"
                  onClick={() => navigate('/research/data-quality-audit')}
                >
                  <BarChart3 className="h-5 w-5" />
                  Open Full Data Quality Audit
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>

              {/* Feature Description */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div className="p-4 rounded-lg border">
                  <div className="font-medium mb-2 flex items-center gap-2">
                    <BarChart3 className="h-4 w-4 text-primary" />
                    Category Wheel View
                  </div>
                  <p className="text-muted-foreground">
                    Visual wheel showing enrichment status by category with color-coded segments
                  </p>
                </div>
                <div className="p-4 rounded-lg border">
                  <div className="font-medium mb-2 flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    Attribute Coverage
                  </div>
                  <p className="text-muted-foreground">
                    See top attributes per category and their coverage percentage
                  </p>
                </div>
                <div className="p-4 rounded-lg border">
                  <div className="font-medium mb-2 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                    Review Queue
                  </div>
                  <p className="text-muted-foreground">
                    Quick links to review suspicious enrichments by category
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Personas Tab */}
        <TabsContent value="personas">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Buyer Personas
              </CardTitle>
              <CardDescription>
                Key buyer personas for your sector, based on market research and behavioral analysis
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingPersonas ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <Card key={i} className="bg-gradient-to-br from-background to-muted/20">
                      <CardHeader className="pb-4">
                        <div className="flex items-center gap-4">
                          <Skeleton className="w-16 h-16 rounded-full" />
                          <div className="flex-1 space-y-2">
                            <Skeleton className="h-5 w-32" />
                            <Skeleton className="h-4 w-24" />
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-3/4" />
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : personas.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {(translatedPersonas.length > 0 ? translatedPersonas : personas).slice(0, 10).map((persona, index) => (
                    <Card key={index} className="bg-gradient-to-br from-background to-muted/20 hover:shadow-lg transition-all duration-300">
                      <CardHeader className="pb-4">
                        <div className="flex items-center gap-4">
                          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden">
                            <img
                              src={persona.image_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(persona.name || `Persona ${index + 1}`)}&background=${['8B5CF6', '3B82F6', '10B981', 'F59E0B', 'EF4444', 'EC4899'][index % 6]}&color=fff&size=200`}
                              alt={persona.name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(persona.name || `Persona ${index + 1}`)}&background=8B5CF6&color=fff&size=200`;
                              }}
                            />
                          </div>
                          <div className="flex-1">
                            <CardTitle className="text-lg">{persona.name}</CardTitle>
                            <p className="text-sm text-muted-foreground">{persona.occupation}</p>
                            {persona.age_range && (
                              <Badge variant="outline" className="mt-1">
                                {persona.age_range}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {persona.description && (
                          <p className="text-sm text-muted-foreground line-clamp-3">
                            {persona.description}
                          </p>
                        )}

                        {persona.buying_behavior && (
                          <div className="space-y-2">
                            <h5 className="text-sm font-medium flex items-center gap-1">
                              <ShoppingBag className="h-3 w-3" />
                              Buying Behavior
                            </h5>
                            <p className="text-xs text-muted-foreground line-clamp-2">
                              {persona.buying_behavior}
                            </p>
                          </div>
                        )}

                        {persona.expectations && (
                          <div className="space-y-2">
                            <h5 className="text-sm font-medium flex items-center gap-1">
                              <Target className="h-3 w-3" />
                              Expectations
                            </h5>
                            <p className="text-xs text-muted-foreground line-clamp-2">
                              {persona.expectations}
                            </p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    No buyer personas found for this sector. Personas are shared across all sites in the same sector.
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Insights Card */}
      <Card className="bg-primary/5 border-primary/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Info className="h-5 w-5" />
            PageMind Intelligence
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm">
            PageMind AI has analyzed and understood your complete offering. This deep knowledge enables our AI to:
          </p>
          <ul className="list-disc list-inside mt-3 space-y-1 text-sm text-muted-foreground">
            <li>Provide accurate product recommendations to customers</li>
            <li>Answer detailed questions about services and pricing</li>
            <li>Guide customers to relevant articles and resources</li>
            <li>Cross-sell and upsell based on customer needs</li>
            <li>Maintain consistency with your brand and offerings</li>
          </ul>
        </CardContent>
      </Card>

      {/* Product Enrichment Comparison Modal */}
      {showEnrichmentModal && enrichedProductData && enrichedProductModalId && (
        <Dialog open={showEnrichmentModal} onOpenChange={setShowEnrichmentModal}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5" />
                AI Product Enrichment Complete
              </DialogTitle>
              <DialogDescription>
                View the original vs AI-enriched product data
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6">
              {/* Product Name */}
              <div>
                <h3 className="font-semibold text-lg">{enrichedProductData.original?.name}</h3>
              </div>

              {/* Product Description - Source of Extraction */}
              {enrichedProductData.original?.description && (
                <div>
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Product Description (Source for AI Extraction)
                  </h4>
                  <Card className="bg-muted/50">
                    <CardContent className="pt-4">
                      <p className="text-sm text-muted-foreground italic">
                        {enrichedProductData.original.description}
                      </p>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Enrichment Statistics */}
              {enrichedProductData.enrichment_stats && (
                <div>
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <Sparkles className="h-4 w-4" />
                    Enrichment Impact
                  </h4>
                  <div className="grid grid-cols-3 gap-3">
                    <Card>
                      <CardContent className="pt-4">
                        <div className="text-center">
                          <Badge variant="outline" className="mb-2">ORIGINAL</Badge>
                          <div className="text-2xl font-bold">{enrichedProductData.enrichment_stats.original_attributes || 0}</div>
                          <div className="text-xs text-muted-foreground mt-1">From Product Feed</div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="border-primary">
                      <CardContent className="pt-4">
                        <div className="text-center">
                          <Badge variant="default" className="mb-2">AI-EXTRACTED</Badge>
                          <div className="text-2xl font-bold text-primary">{enrichedProductData.enrichment_stats.ai_extracted_attributes || 0}</div>
                          <div className="text-xs text-muted-foreground mt-1">Added by AI</div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="border-green-500">
                      <CardContent className="pt-4">
                        <div className="text-center">
                          <Badge variant="outline" className="mb-2 border-green-500 text-green-600">ENRICHMENT</Badge>
                          <div className="text-2xl font-bold text-green-600">+{enrichedProductData.enrichment_stats.enrichment_percentage || 0}%</div>
                          <div className="text-xs text-muted-foreground mt-1">Value Added</div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              )}

              {/* All Attributes with Origin Badges */}
              <div>
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  All Attributes with Origin Tracking
                </h4>
                <Card>
                  <CardContent className="pt-4">
                    {(() => {
                      const attributeOrigins = enrichedProductData.attribute_origins || {};
                      const allAttributes = enrichedProductData.enriched?.attributes || {};

                      // Industrial-feed pattern: when feed already covers the canonical
                      // schema (e.g., Doga's CSV with 30 spec columns), the AI correctly
                      // returns 0 new attrs. enriched.attributes is only AI-extracted, so
                      // checking it alone hides the 30 feed-origin attrs that ARE present
                      // in attribute_origins. Check the union to gate the empty state.
                      if (Object.keys(allAttributes).length === 0 && Object.keys(attributeOrigins).length === 0) {
                        return <p className="text-sm text-muted-foreground">No attributes available</p>;
                      }

                      // Group by origin
                      const originalAttrs = Object.entries(attributeOrigins)
                        .filter(([_, data]: [string, any]) => data.origin === 'original');
                      const aiAttrs = Object.entries(attributeOrigins)
                        .filter(([_, data]: [string, any]) => data.origin === 'ai_extracted');
                      const unknownAttrs = Object.entries(allAttributes)
                        .filter(([key]) => !attributeOrigins[key]);

                      return (
                        <div className="space-y-4 text-sm max-h-96 overflow-y-auto">
                          {aiAttrs.length > 0 && (
                            <div>
                              <div className="flex items-center gap-2 mb-2">
                                <Badge variant="default" className="text-xs">AI-EXTRACTED</Badge>
                                <span className="text-xs text-muted-foreground">({aiAttrs.length} attributes)</span>
                              </div>
                              <div className="space-y-1 pl-2">
                                {aiAttrs.map(([key, data]: [string, any]) => (
                                  <div key={key} className="flex justify-between items-start gap-4 p-2 bg-primary/5 rounded text-xs">
                                    <span className="font-medium">{key}:</span>
                                    <span className="text-right flex-1 text-muted-foreground">
                                      {typeof data.value === 'object' ? JSON.stringify(data.value) : String(data.value)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {originalAttrs.length > 0 && (
                            <div>
                              <div className="flex items-center gap-2 mb-2">
                                <Badge variant="outline" className="text-xs">ORIGINAL</Badge>
                                <span className="text-xs text-muted-foreground">({originalAttrs.length} attributes)</span>
                              </div>
                              <div className="space-y-1 pl-2">
                                {originalAttrs.map(([key, data]: [string, any]) => (
                                  <div key={key} className="flex justify-between items-start gap-4 p-2 bg-muted/30 rounded text-xs">
                                    <span className="font-medium">{key}:</span>
                                    <span className="text-right flex-1 text-muted-foreground">
                                      {typeof data.value === 'object' ? JSON.stringify(data.value) : String(data.value)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {unknownAttrs.length > 0 && (
                            <div>
                              <div className="flex items-center gap-2 mb-2">
                                <Badge variant="secondary" className="text-xs">UNKNOWN ORIGIN</Badge>
                                <span className="text-xs text-muted-foreground">({unknownAttrs.length} attributes)</span>
                              </div>
                              <div className="space-y-1 pl-2">
                                {unknownAttrs.map(([key, value]) => (
                                  <div key={key} className="flex justify-between items-start gap-4 p-2 bg-muted/50 rounded text-xs">
                                    <span className="font-medium">{key}:</span>
                                    <span className="text-right flex-1 text-muted-foreground">
                                      {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </CardContent>
                </Card>
              </div>

              {/* Benefit Scores */}
              {enrichedProductData.enriched?.benefit_scores && Object.keys(enrichedProductData.enriched.benefit_scores).length > 0 && (
                <div>
                  <h4 className="font-medium mb-3">Benefit Scores</h4>
                  <div className="space-y-2">
                    {Object.entries(enrichedProductData.enriched.benefit_scores)
                      .sort((a: any, b: any) => (b[1]?.score || 0) - (a[1]?.score || 0))
                      .slice(0, 5)
                      .map(([benefit, data]: [string, any]) => (
                        <div key={benefit} className="space-y-1">
                          <div className="flex justify-between text-sm">
                            <span className="font-medium">{benefit}</span>
                            <span>{(data?.score || 0).toFixed(1)}%</span>
                          </div>
                          <div className="h-2 bg-secondary rounded-full overflow-hidden">
                            <div
                              className="h-full bg-primary transition-all"
                              style={{ width: `${data?.score || 0}%` }}
                            />
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer with navigation to Enrichment Review (Story 358) */}
            <DialogFooter className="mt-6">
              <Button
                variant="outline"
                onClick={() => {
                  setShowEnrichmentModal(false);
                  navigate(`/enrichment-review?product=${enrichedProductModalId}`);
                }}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Go to Enrichment Review
              </Button>
              {/* ⛔ THE FORCE PATH NEEDS A BUTTON WHERE THE OPERATOR IS STANDING. This modal
                  is exactly where someone lands when the product is already enriched — the
                  case that previously had no way to re-run at all. Putting the affordance
                  only in the batch toolbar would leave the capability unreachable from here,
                  which is the same as not shipping it. */}
              <Button
                variant="outline"
                disabled={enrichingProduct === enrichedProductModalId}
                onClick={() => {
                  setShowEnrichmentModal(false);
                  // ⇒ The kind is looked up from the SAME listing that produced the row, not assumed.
                  // If the row has been paged away the lookup yields undefined and the guard refuses,
                  // which is the honest outcome: this surface cannot establish provenance on its own.
                  handleEnrichProduct(
                    enrichedProductModalId!,
                    products.find((p) => p.id === enrichedProductModalId)?.id_kind,
                    true
                  );
                }}
                title="Run AI enrichment again for this product"

              >
                <Sparkles className="h-4 w-4 mr-2" />
                {enrichingProduct === enrichedProductModalId ? 'Re-running…' : 'Re-run Enrichment'}
              </Button>
              <Button onClick={() => setShowEnrichmentModal(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* AEO Generation Confirmation Dialog */}
      <AlertDialog open={!!aeoConfirmProduct} onOpenChange={(open) => { if (!open) setAeoConfirmProduct(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Trigger AEO Generation?</AlertDialogTitle>
            <AlertDialogDescription>
              This product is not yet AEO generated. Do you want to trigger the AEO generation now?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>No</AlertDialogCancel>
            <AlertDialogAction onClick={() => {
              const product = aeoConfirmProduct;
              setAeoConfirmProduct(null);
              handleGenerateAEOContent(product);
            }}>
              Yes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* AEO Content Modal */}
      {showAEOModal && (
        <Dialog open={showAEOModal} onOpenChange={(open) => {
          if (!generatingAEO) setShowAEOModal(open);
        }}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Zap className="h-5 w-5 text-yellow-500" />
                AI-Enhanced Product Page (AEO)
              </DialogTitle>
              <DialogDescription>
                Content optimized for AI search engines like ChatGPT and Perplexity
              </DialogDescription>
            </DialogHeader>

            {/* Progress indicator during generation */}
            {generatingAEO && aeoProgress && (
              <div className="space-y-4 py-8">
                <div className="flex items-center justify-center gap-3">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <span className="text-lg font-medium">{aeoProgress.phase}</span>
                </div>
                <div className="w-full bg-secondary rounded-full h-3 overflow-hidden">
                  <div
                    className="h-full bg-primary transition-all duration-500"
                    style={{ width: `${aeoProgress.percent}%` }}
                  />
                </div>
                <p className="text-center text-muted-foreground text-sm">
                  Generating optimized content... This may take a moment.
                </p>
              </div>
            )}

            {/* Generated content display - using ProductAEOViewer for consistent rendering */}
            {!generatingAEO && aeoContent && (
              <div className="space-y-6">
                <ProductAEOViewer
                  title={currentAEOProduct?.name || aeoContent.product_name || 'Product'}
                  content={aeoContent.sections || aeoContent}
                  metadata={{
                    product_url: currentAEOProduct?.url,
                    image_url: currentAEOProduct?.image_url,
                    price: currentAEOProduct?.price,
                    currency: currentAEOProduct?.currency
                  }}
                  generatedAt={aeoContent.generated_at || aeoContent.cached_at}
                  generationMetadata={aeoContent.generation_metadata}
                  isSuperAdmin={user?.role === 'super_admin'}
                  showCopyButtons={true}
                />

                {/* Action buttons */}
                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowAEOModal(false);
                      setAeoContent(null);
                    }}
                  >
                    Close
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      // Copy JSON to clipboard
                      navigator.clipboard.writeText(JSON.stringify(aeoContent, null, 2));
                      toast({
                        title: 'Copied!',
                        description: 'AEO content JSON copied to clipboard',
                      });
                    }}
                  >
                    Copy JSON
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      if (currentAEOProduct) {
                        setShowAEOModal(false);
                        navigate(`/content-review?type=product_aeo&id=${currentAEOProduct.aeo_content_product_id || currentAEOProduct.product_feed_id || currentAEOProduct.id}`);
                      }
                    }}
                    disabled={!currentAEOProduct}
                    className="border-green-300 dark:border-green-700 text-green-700 dark:text-green-300 hover:bg-green-50 dark:hover:bg-green-950"
                  >
                    <Shield className="h-4 w-4 mr-2" />
                    Quality Assurance
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      if (currentAEOProduct) {
                        setShowAEOModal(false);
                        navigate(`/research/competitive-benchmark?sourceType=product&sourceId=${currentAEOProduct.id}&sourceName=${encodeURIComponent(currentAEOProduct.name)}`);
                      }
                    }}
                    disabled={!currentAEOProduct}
                    className="border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-950"
                  >
                    <BarChart3 className="h-4 w-4 mr-2" />
                    Benchmark
                  </Button>
                  <Button
                    onClick={() => {
                      if (currentAEOProduct) {
                        handleGenerateAEOContent(currentAEOProduct, true);
                      }
                    }}
                    disabled={!currentAEOProduct || generatingAEO !== null}
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Regenerate
                  </Button>
                </div>
              </div>
            )}

            {/* Error state or empty content */}
            {!generatingAEO && !aeoContent && (
              <div className="py-8 text-center text-muted-foreground">
                <p>No content generated yet.</p>
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={() => setShowAEOModal(false)}
                >
                  Close
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* Category AEO Content Modal */}
      {showCategoryAEOModal && (
        <Dialog open={showCategoryAEOModal} onOpenChange={(open) => {
          if (!generatingCategoryAEO) setShowCategoryAEOModal(open);
        }}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Zap className="h-5 w-5 text-purple-500" />
                Category AEO Content
              </DialogTitle>
              <DialogDescription>
                {currentAEOCategory && (
                  <span className="font-medium">{currentAEOCategory.split(' > ').pop()}</span>
                )}
                {' - '}Content optimized for AI search engines
              </DialogDescription>
            </DialogHeader>

            {/* Loading State */}
            {generatingCategoryAEO && (
              <div className="space-y-4 py-8">
                <div className="flex items-center justify-center gap-3">
                  <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
                  <span className="text-lg font-medium">Generating AEO Content...</span>
                </div>
                <p className="text-center text-muted-foreground text-sm">
                  Analyzing category data, products, and generating optimized content...
                </p>
              </div>
            )}

            {/* Generated Content */}
            {!generatingCategoryAEO && categoryAEOContent && (
              <div className="space-y-6">
                {/* Status Bar with Save/Approval */}
                <div className="flex items-center justify-between p-3 bg-muted rounded-lg gap-4">
                  <div className="flex items-center gap-3 flex-wrap">
                    {categoryAEOContent.quality && (
                      <>
                        <Badge variant={categoryAEOContent.quality.grade === 'A' ? 'default' : categoryAEOContent.quality.grade === 'B' ? 'secondary' : 'outline'}>
                          Grade: {categoryAEOContent.quality.grade}
                        </Badge>
                        <span className="text-sm text-muted-foreground">
                          {categoryAEOContent.quality.sections_complete}/{categoryAEOContent.quality.sections_total} sections
                        </span>
                      </>
                    )}
                    {categoryAEOMetadata && (
                      <Badge
                        variant={
                          categoryAEOMetadata.status === 'approved' ? 'default' :
                          categoryAEOMetadata.status === 'rejected' ? 'destructive' :
                          categoryAEOMetadata.status === 'pending_review' ? 'secondary' : 'outline'
                        }
                        className={
                          categoryAEOMetadata.status === 'approved' ? 'bg-green-600' : ''
                        }
                      >
                        {categoryAEOMetadata.status === 'approved' && '✓ '}
                        {categoryAEOMetadata.status?.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                        {categoryAEOMetadata.version > 1 && ` (v${categoryAEOMetadata.version})`}
                      </Badge>
                    )}
                    {categoryAEOFeedbackSummary && (categoryAEOFeedbackSummary.thumbsUp > 0 || categoryAEOFeedbackSummary.thumbsDown > 0) && (
                      <span className="flex items-center gap-2 text-xs text-muted-foreground">
                        <ThumbsUp className="h-3 w-3 text-green-600" /> {categoryAEOFeedbackSummary.thumbsUp}
                        <ThumbsDown className="h-3 w-3 text-red-600" /> {categoryAEOFeedbackSummary.thumbsDown}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {!categoryAEOMetadata && (
                      <Button
                        variant="default"
                        size="sm"
                        onClick={handleSaveCategoryAEO}
                        disabled={savingCategoryAEO}
                      >
                        {savingCategoryAEO ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Save className="h-4 w-4 mr-2" />
                        )}
                        Save
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleGenerateCategoryAEO(currentAEOCategory!, true)}
                      disabled={generatingCategoryAEO}
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Regenerate
                    </Button>
                  </div>
                </div>

                {/* Approval Panel (if content is saved) */}
                {categoryAEOMetadata && (
                  <AEOApprovalPanel
                    contentId={categoryAEOMetadata.id}
                    metadata={categoryAEOMetadata}
                    feedbackSummary={categoryAEOFeedbackSummary}
                    onStatusChange={handleCategoryAEOStatusChange}
                    onRegenerate={() => handleGenerateCategoryAEO(currentAEOCategory!, true)}
                  />
                )}

                {/* Featured Snippet */}
                {categoryAEOContent.sections?.featured_snippet && (
                  <Card className="border-purple-200 dark:border-purple-800">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Star className="h-4 w-4 text-yellow-500" />
                        Featured Snippet
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                        <p className="text-sm">{categoryAEOContent.sections.featured_snippet}</p>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Category Purpose */}
                {categoryAEOContent.sections?.category_purpose && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Why This Category Exists</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground">{categoryAEOContent.sections.category_purpose}</p>
                    </CardContent>
                  </Card>
                )}

                {/* Buyer Guide */}
                {categoryAEOContent.sections?.buyer_guide?.rules?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Target className="h-4 w-4" />
                        {categoryAEOContent.sections.buyer_guide.intro || 'How to Choose'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {categoryAEOContent.sections.buyer_guide.rules.map((rule: any, i: number) => (
                          <div key={i} className="flex items-start gap-2 text-sm">
                            <ChevronRight className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                            <div>
                              <span className="font-medium">{rule.profile}</span>
                              <span className="text-muted-foreground"> → {rule.recommendation}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Differentiators */}
                {categoryAEOContent.sections?.differentiators?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Key Differentiators</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-1">
                        {categoryAEOContent.sections.differentiators.map((diff: string, i: number) => (
                          <li key={i} className="flex items-start gap-2 text-sm">
                            <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                            <span>{diff}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}

                {/* Use Cases (Best For) */}
                {categoryAEOContent.sections?.use_cases?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Best For</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid gap-3">
                        {categoryAEOContent.sections.use_cases.map((uc: any, i: number) => (
                          <div key={i} className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                            <p className="font-medium text-sm">{uc.use_case || uc.name}</p>
                            {uc.description && <p className="text-sm text-muted-foreground mt-1">{uc.description}</p>}
                            {uc.recommendation && (
                              <Badge variant="secondary" className="mt-2 text-xs">
                                Recommended: {uc.recommendation}
                              </Badge>
                            )}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Best Sellers */}
                {categoryAEOContent.sections?.best_sellers?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Top Products</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {categoryAEOContent.sections.best_sellers.map((product: any, i: number) => (
                          <div key={i} className="flex items-start gap-3 p-2 border rounded-lg">
                            <div className="flex-1">
                              <p className="font-medium text-sm">{product.title}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <Badge variant="outline" className="text-xs">{product.brand}</Badge>
                                <span className="text-sm font-medium">{product.price} {product.currency}</span>
                              </div>
                              {product.key_features?.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-2">
                                  {product.key_features.map((f: any, fi: number) => (
                                    <Badge key={fi} variant="secondary" className="text-xs">
                                      {f.attribute}: {f.value}
                                    </Badge>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* FAQs */}
                {categoryAEOContent.sections?.faqs?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <HelpCircle className="h-4 w-4" />
                        Frequently Asked Questions
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {categoryAEOContent.sections.faqs.map((faq: any, i: number) => (
                          <div key={i} className="border-b last:border-0 pb-3 last:pb-0">
                            <p className="font-medium text-sm">{faq.question}</p>
                            <p className="text-sm text-muted-foreground mt-1">{faq.answer}</p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* AEO Questions Management - Sync to Question Wheel */}
                {categoryAEOMetadata && selectedSite?.id && (
                  <AEOQuestionsPanel
                    siteId={selectedSite.id}
                    contentId={categoryAEOMetadata.id}
                    categoryPath={currentAEOCategory}
                    onSync={(wheelId) => {
                      toast({
                        title: 'Question Wheel Updated',
                        description: 'Questions synced to wheel successfully',
                      });
                    }}
                  />
                )}

                {/* Competitive Comparison */}
                {categoryAEOContent.sections?.competitive_comparison?.brands?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Brand Comparison</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {categoryAEOContent.sections.competitive_comparison.brands.map((brand: any, i: number) => (
                          <div key={i} className="p-2 bg-muted/50 rounded-lg">
                            <p className="font-medium text-sm">{brand.brand || brand.name}</p>
                            {brand.positioning && <p className="text-sm text-muted-foreground">{brand.positioning}</p>}
                            {brand.best_for && <Badge variant="secondary" className="mt-1 text-xs">Best for: {brand.best_for}</Badge>}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Narrative */}
                {categoryAEOContent.sections?.narrative && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Category Story</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                        {categoryAEOContent.sections.narrative}
                      </p>
                    </CardContent>
                  </Card>
                )}

                {/* AI Context Summary */}
                {categoryAEOContent.sections?.ai_context_summary && (
                  <Card className="border-blue-200 dark:border-blue-800">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-blue-500" />
                        AI Context Summary
                      </CardTitle>
                      <CardDescription className="text-xs">
                        Structured metadata for AI engines
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div><span className="font-medium">Category:</span> {categoryAEOContent.sections.ai_context_summary.category}</div>
                        <div><span className="font-medium">Store:</span> {categoryAEOContent.sections.ai_context_summary.store}</div>
                        <div><span className="font-medium">Products:</span> {categoryAEOContent.sections.ai_context_summary.product_count}</div>
                        <div><span className="font-medium">Price Range:</span> {categoryAEOContent.sections.ai_context_summary.price_range}</div>
                        {categoryAEOContent.sections.ai_context_summary.top_brands?.length > 0 && (
                          <div className="col-span-2">
                            <span className="font-medium">Top Brands:</span> {categoryAEOContent.sections.ai_context_summary.top_brands.join(', ')}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Generation Info */}
                <div className="text-xs text-muted-foreground text-right">
                  Generated: {new Date(categoryAEOContent.generated_at).toLocaleString()}
                  {categoryAEOContent.from_cache && ' (cached)'}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* Category Detail Panel - Use CategoryLeafDetail component */}
      {categoryDetailPanel.open && categoryDetailPanel.category && selectedSectorId && selectedSite?.id && (
        <CategoryLeafDetail
          category={{
            id: categoryDetailPanel.category.id,
            category_name: categoryDetailPanel.category.category_name,
            category_path: categoryDetailPanel.category.category_path,
            full_path: categoryDetailPanel.category.full_path,
            hasChildren: categoryDetailPanel.category.hasChildren || false,
          }}
          sectorId={selectedSectorId}
          siteId={selectedSite.id}
          isOpen={categoryDetailPanel.open}
          onClose={() => setCategoryDetailPanel({ open: false, category: null })}
          onNavigateToTab={(tab: string, data?: any) => {
            // Handle navigation - switch to the specified tab
            if (tab === 'products') {
              if (data?.subcategory) {
                // Filter by subcategory (site-specific Spanish category)
                handleProductCountClick('', data.subcategory);
                setCategoryDetailPanel({ open: false, category: null });
              } else if (data?.categoryPath) {
                // Filter by category (Google taxonomy)
                handleProductCountClick(data.categoryPath);
                setCategoryDetailPanel({ open: false, category: null });
              }
            }
            setActiveTab(tab);
          }}
          onTriggerResearch={(categoryName: string) => {
            // Trigger research for this category
            toast({
              title: 'Research Triggered',
              description: `Research for "${categoryName}" has been queued`,
            });
          }}
        />
      )}

      {/* SEO Visibility Panel (Story 56) */}
      {visibilityProduct && selectedSite && (
        <VisibilityPanel
          productId={visibilityProduct.id}
          productName={visibilityProduct.name}
          productUrl={visibilityProduct.url}
          siteId={selectedSite.id}
          open={!!visibilityProduct}
          onOpenChange={(open) => !open && setVisibilityProduct(null)}
        />
      )}

      {/* ⛔⛔ THE PICKER FEEDS THE SAME `target` STATE THE TOOLBAR ALREADY READS — it does not enrich
          on its own and it does not keep a second selection. Two selections would be two sources of
          truth, and "Enrich Selected (N)" would report one of them while the operator was looking at
          the other; the honest-count work in this file exists because that class of divergence is
          how a badge starts lying. ⇒ The picker CHOOSES, the existing (already p4-verified) enrich
          path ACTS, and there is exactly one of each. (task-dx7eahgnvt)
          ⚠️ allowCohortSelection is left at its default of false: the cohort affordance stays dark
          until an endpoint can resolve a filter kind-awarely, which productTarget.ts refuses to
          assume — sending a cohort to a kbe-id endpoint would silently do partial or zero work. */}
      {selectedSite?.id && (
        <ProductTargetPicker
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          siteId={selectedSite.id}
          idKind="kbe"
          actionLabel="Select for enrichment"
          showForce
          forceLabel="Force re-enrich"
          onConfirm={(picked, options) => applyPickerConfirm(picked, options, {
            setExplicitIds: target.setExplicitIds,
            setForce: setForceReEnrich,
            close: () => setPickerOpen(false),
          })}
        />
      )}
    </div>
  );
}

// TreeNode Component for Category Tree View
interface TreeNodeProps {
  category: any;
  level?: number;
  onProductCountClick: (categoryPath: string) => void;
  onCategoryClick?: (category: any) => void;
  siteId?: string;
}

function TreeNode({ category, level = 0, onProductCountClick, onCategoryClick, siteId }: TreeNodeProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasChildren = category.children && category.children.length > 0;
  const enrichedCount = category.enriched_count || 0;
  const productCount = category.product_count || 0;
  const enrichmentPercent = productCount > 0 ? Math.round((enrichedCount / productCount) * 100) : 0;

  const handleRowClick = () => {
    // Open category detail panel (similar to sector categories)
    if (onCategoryClick) {
      onCategoryClick(category);
    }
  };

  return (
    <div className="border rounded-lg overflow-hidden">
      <div
        className={`flex items-center gap-3 p-3 hover:bg-accent/50 cursor-pointer transition-colors ${
          level > 0 ? 'ml-6' : ''
        }`}
        style={{ paddingLeft: `${level * 1.5 + 1}rem` }}
        onClick={handleRowClick}
      >
        {hasChildren && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            className="flex-shrink-0"
          >
            {isExpanded ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )}
          </button>
        )}
        {!hasChildren && <div className="w-4" />}

        <div className="flex-1 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="font-medium">{category.category_name}</span>
            {category.is_leaf && (
              <Badge variant="outline" className="text-xs">
                Leaf
              </Badge>
            )}
          </div>

          <div className="flex items-center gap-2">
            {hasChildren && (
              <Badge variant="secondary" className="text-xs">
                {category.subcategory_count} subcategories
              </Badge>
            )}
            {/* Enrichment Stats Badge */}
            {productCount > 0 && (
              <Badge
                variant="outline"
                className={`text-xs ${
                  enrichmentPercent === 100
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                    : enrichmentPercent > 50
                    ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                    : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                }`}
              >
                <Sparkles className="h-3 w-3 mr-1" />
                {enrichedCount}/{productCount} AI ({enrichmentPercent}%)
              </Badge>
            )}
            <Badge
              variant="default"
              className="cursor-pointer hover:bg-primary/80 transition-colors"
            >
              {productCount} products
            </Badge>
          </div>
        </div>
      </div>

      {hasChildren && isExpanded && (
        <div className="bg-accent/20 border-t">
          {category.children.map((child: any) => (
            <TreeNode
              key={child.id}
              category={child}
              level={level + 1}
              onProductCountClick={onProductCountClick}
              onCategoryClick={onCategoryClick}
              siteId={siteId}
            />
          ))}
        </div>
      )}
    </div>
  );
}
